import { useState, useEffect, useCallback, useRef, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";
import DOMPurify from "dompurify";
import {
  EnvelopeSimple, MagnifyingGlass, Paperclip, Star, SignOut, Tag,
  FolderOpen, PaperPlaneTilt, Tray, Archive, X, Plus, CaretDown,
  FilePdf, FileDoc, FileXls, FileZip, File, DownloadSimple,
  Funnel, EnvelopeOpen, ChartBar, ShieldCheck, Buildings, At,
  Eye, EyeSlash, ArrowLeft, Heart, Gear, Plug, TestTube,
  ArrowsClockwise, Trash, CheckCircle, XCircle, Warning, CircleNotch,
  Users, UserPlus, PencilSimple, Crown, TagSimple,
  PaperPlaneTilt as PaperPlaneIcon, Upload, FileArrowUp, Spinner,
  CalendarBlank, Funnel as FunnelIcon, XCircle as XCircleIcon2,
  Signature, Code, Star as StarIcon,
  FloppyDisk, CloudArrowDown, Clock,
  ListBullets, ShieldWarning, FileCsv
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ baseURL: API });

// Restore token from localStorage on page load
const savedToken = localStorage.getItem("auth_token");
if (savedToken) {
  ax.defaults.headers.common["Authorization"] = `Bearer ${savedToken}`;
}

const AuthContext = createContext(null);

function formatApiError(detail) {
  if (!detail) return "Errore sconosciuto";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

function formatDate(d) {
  const date = new Date(d);
  const now = new Date();
  const diff = now - date;
  if (diff < 86400000 && now.getDate() === date.getDate()) {
    return date.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  }
  if (diff < 604800000) {
    return date.toLocaleDateString("it-IT", { weekday: "short", hour: "2-digit", minute: "2-digit" });
  }
  return date.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}

function formatFullDate(d) {
  return new Date(d).toLocaleDateString("it-IT", {
    weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit"
  });
}

function formatSize(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1048576) return (bytes / 1024).toFixed(0) + " KB";
  return (bytes / 1048576).toFixed(1) + " MB";
}

function getFileIcon(type) {
  if (!type) return <File size={18} />;
  if (type.includes("pdf")) return <FilePdf size={18} weight="duotone" />;
  if (type.includes("word") || type.includes("doc")) return <FileDoc size={18} weight="duotone" />;
  if (type.includes("sheet") || type.includes("xls")) return <FileXls size={18} weight="duotone" />;
  if (type.includes("zip")) return <FileZip size={18} weight="duotone" />;
  return <File size={18} />;
}

function getInitials(name) {
  return name?.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "??";
}

// ---- TELCAVOIP BADGE ----
function TelcaBadge({ style }) {
  return (
    <a href="https://telcavoip.ai" target="_blank" rel="noopener noreferrer" className="telca-badge" style={style} data-testid="telcavoip-badge">
      <Heart size={12} weight="fill" />
      <span>Made With Love By TelcaVoIP AI</span>
    </a>
  );
}

// ---- LOGIN ----
function LoginPage() {
  const { login } = useContext(AuthContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [view, setView] = useState("login"); // "login" | "forgot" | "forgot-sent" | "2fa"
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);

  const doLoginRequest = async (extra = {}) => {
    const { data } = await ax.post("/auth/login", { email, password, ...extra });
    if (data.requires_2fa) {
      setView("2fa"); setError(""); setTotpCode("");
      return;
    }
    login(data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await doLoginRequest();
    } catch (err) {
      if (err.response) setError(formatApiError(err.response?.data?.detail) || "Credenziali non valide");
      else if (err.request) setError("Impossibile contattare il server. Verifica la connessione di rete.");
      else setError("Errore di connessione al server");
    } finally { setLoading(false); }
  };

  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const clean = totpCode.replace(/\s+/g, "");
      await doLoginRequest(useBackupCode ? { backup_code: clean } : { totp_code: clean });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Codice non valido");
    } finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMsg("");
    try {
      await ax.post("/auth/forgot-password", { email: forgotEmail });
      setView("forgot-sent");
    } catch (err) {
      setForgotMsg(formatApiError(err.response?.data?.detail) || "Errore nell'invio");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="login-container" data-testid="login-page">
      <div className="login-visual" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1666618207644-4de0226a3f85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwbW9kZXJuJTIwb2ZmaWNlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3NjE5Mjk3NXww&ixlib=rb-4.1.0&q=85)" }}>
        <div className="login-visual-overlay" />
        <div className="login-visual-text">
          <h1>Archivio Email<br />Centralizzato</h1>
          <p>Studio Legale Zilioli — Verona</p>
        </div>
      </div>
      <div className="login-form-section">
        <div className="login-form-wrapper">
          {view === "login" && (
            <>
              <h2>Accedi al Sistema</h2>
              <p>Inserisci le credenziali per accedere all'archivio</p>
              {error && <div className="login-error" data-testid="login-error">{error}</div>}
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Email</label>
                  <input data-testid="login-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="nome@studiozilioli.it" required />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <div className="password-input-wrapper">
                    <input data-testid="login-password-input" type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Inserisci la password" required />
                    <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)} data-testid="password-toggle-btn" tabIndex={-1}>
                      {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div className="forgot-link-row">
                  <button type="button" className="forgot-link" onClick={() => { setView("forgot"); setError(""); }} data-testid="forgot-password-link">
                    Password dimenticata?
                  </button>
                </div>
                <button className="login-btn" type="submit" disabled={loading} data-testid="login-submit-btn">
                  {loading ? "Accesso in corso..." : "Accedi"}
                </button>
              </form>
            </>
          )}

          {view === "2fa" && (
            <>
              <button className="back-to-login" onClick={() => { setView("login"); setError(""); setTotpCode(""); setUseBackupCode(false); }} data-testid="back-to-login-from-2fa">
                <ArrowLeft size={16} /> Annulla
              </button>
              <h2>Verifica in due passaggi</h2>
              <p>{useBackupCode
                ? "Inserisci uno dei tuoi codici di backup (formato XXXX-XXXX)."
                : "Apri Google Authenticator, Authy o l'app che hai usato per il setup e inserisci il codice a 6 cifre."}</p>
              {error && <div className="login-error" data-testid="totp-error">{error}</div>}
              <form onSubmit={handleTotpSubmit}>
                <div className="form-group">
                  <label>{useBackupCode ? "Codice di backup" : "Codice a 6 cifre"}</label>
                  <input data-testid="totp-code-input" type="text" value={totpCode} onChange={e => setTotpCode(e.target.value)}
                    placeholder={useBackupCode ? "XXXX-XXXX" : "000000"} autoFocus maxLength={useBackupCode ? 9 : 6} required
                    style={{ fontSize: "1.5rem", letterSpacing: "0.3em", textAlign: "center", fontFamily: "monospace" }} />
                </div>
                <div style={{ marginBottom: 12, fontSize: "0.8rem" }}>
                  <button type="button" className="forgot-link" onClick={() => { setUseBackupCode(v => !v); setTotpCode(""); setError(""); }} data-testid="toggle-backup-code">
                    {useBackupCode ? "← Usa il codice dall'app authenticator" : "Non hai accesso all'app? Usa un codice di backup"}
                  </button>
                </div>
                <button className="login-btn" type="submit" disabled={loading} data-testid="totp-submit-btn">
                  {loading ? "Verifica in corso..." : "Verifica e Accedi"}
                </button>
              </form>
            </>
          )}

          {view === "forgot" && (
            <>
              <button className="back-to-login" onClick={() => { setView("login"); setForgotMsg(""); }} data-testid="back-to-login-btn">
                <ArrowLeft size={16} /> Torna al login
              </button>
              <h2>Recupera Password</h2>
              <p>Inserisci il tuo indirizzo email e ti invieremo le istruzioni per reimpostare la password.</p>
              {forgotMsg && <div className="login-error" data-testid="forgot-error">{forgotMsg}</div>}
              <form onSubmit={handleForgotPassword}>
                <div className="form-group">
                  <label>Email</label>
                  <input data-testid="forgot-email-input" type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="nome@studiozilioli.it" required />
                </div>
                <button className="login-btn" type="submit" disabled={forgotLoading} data-testid="forgot-submit-btn">
                  {forgotLoading ? "Invio in corso..." : "Invia Link di Recupero"}
                </button>
              </form>
            </>
          )}

          {view === "forgot-sent" && (
            <>
              <div className="forgot-success" data-testid="forgot-success">
                <EnvelopeSimple size={40} weight="duotone" style={{ color: "var(--accent-success)", marginBottom: 12 }} />
                <h2>Email Inviata</h2>
                <p>Se l'indirizzo <strong>{forgotEmail}</strong> e' presente nel nostro sistema, riceverai un'email con le istruzioni per reimpostare la password.</p>
                <p style={{ fontSize: "0.75rem", color: "var(--text-tertiary)", marginTop: 16 }}>Controlla anche la cartella spam.</p>
                <button className="login-btn" style={{ marginTop: 24 }} onClick={() => { setView("login"); setForgotEmail(""); }} data-testid="back-to-login-after-forgot">
                  Torna al Login
                </button>
              </div>
            </>
          )}

          <TelcaBadge style={{ marginTop: 32 }} />
        </div>
      </div>
    </div>
  );
}

// ---- SIDEBAR ----
function Sidebar({ accounts, tags, activeAccount, setActiveAccount, activeTag, setActiveTag, activeView, setActiveView, user, onLogout, stats, onSyncAccount, syncingAccountId }) {
  return (
    <div className="sidebar" data-testid="sidebar">
      <div className="sidebar-header">
        <p className="sidebar-logo">Archivio Email</p>
        <p className="sidebar-subtitle">Studio Legale Zilioli</p>
      </div>

      <div className="sidebar-section">
        <p className="sidebar-section-title">Navigazione</p>
        <button data-testid="nav-all-emails" className={`sidebar-item ${activeView === "all" ? "active" : ""}`} onClick={() => { setActiveView("all"); setActiveAccount(null); setActiveTag(null); }}>
          <Tray size={16} /> Tutte le Email
          <span className="sidebar-item-count">{stats?.total_emails || 0}</span>
        </button>
        <button data-testid="nav-unread" className={`sidebar-item ${activeView === "unread" ? "active" : ""}`} onClick={() => setActiveView("unread")}>
          <EnvelopeSimple size={16} /> Non Lette
          <span className="sidebar-item-count">{stats?.unread || 0}</span>
        </button>
        <button data-testid="nav-attachments" className={`sidebar-item ${activeView === "attachments" ? "active" : ""}`} onClick={() => setActiveView("attachments")}>
          <Paperclip size={16} /> Con Allegati
          <span className="sidebar-item-count">{stats?.total_attachments || 0}</span>
        </button>
        <button data-testid="nav-stats" className={`sidebar-item ${activeView === "stats" ? "active" : ""}`} onClick={() => setActiveView("stats")}>
          <ChartBar size={16} /> Statistiche
        </button>
        <button data-testid="nav-tags" className={`sidebar-item ${activeView === "tags-manage" ? "active" : ""}`} onClick={() => setActiveView("tags-manage")}>
          <TagSimple size={16} /> Gestione Pratiche
        </button>
        <button data-testid="nav-compose" className={`sidebar-item ${activeView === "compose" ? "active" : ""}`} onClick={() => setActiveView("compose")}>
          <PaperPlaneIcon size={16} /> Componi Email
        </button>
        <button data-testid="nav-signatures" className={`sidebar-item ${activeView === "signatures" ? "active" : ""}`} onClick={() => setActiveView("signatures")}>
          <Signature size={16} /> Gestione Firme
        </button>
        {user?.role === "admin" && (
          <button data-testid="nav-import" className={`sidebar-item ${activeView === "import" ? "active" : ""}`} onClick={() => setActiveView("import")}>
            <FileArrowUp size={16} /> Importa Outlook
          </button>
        )}
        {user?.role === "admin" && (
          <button data-testid="nav-config" className={`sidebar-item ${activeView === "config" ? "active" : ""}`} onClick={() => setActiveView("config")}>
            <Gear size={16} /> Configurazione
          </button>
        )}
        {user?.role === "admin" && (
          <button data-testid="nav-users" className={`sidebar-item ${activeView === "users" ? "active" : ""}`} onClick={() => setActiveView("users")}>
            <Users size={16} /> Gestione Utenti
          </button>
        )}
        {user?.role === "admin" && (
          <button data-testid="nav-backup" className={`sidebar-item ${activeView === "backup" ? "active" : ""}`} onClick={() => setActiveView("backup")}>
            <FloppyDisk size={16} /> Backup Database
          </button>
        )}
        {user?.role === "admin" && (
          <button data-testid="nav-audit" className={`sidebar-item ${activeView === "audit" ? "active" : ""}`} onClick={() => setActiveView("audit")}>
            <ListBullets size={16} /> Audit Log
          </button>
        )}
        <button data-testid="nav-security" className={`sidebar-item ${activeView === "security" ? "active" : ""}`} onClick={() => setActiveView("security")}>
          <ShieldCheck size={16} /> Sicurezza (2FA)
        </button>
      </div>

      <div className="sidebar-section sidebar-accounts">
        <p className="sidebar-section-title" style={{ fontSize: "0.75rem", fontWeight: 900, color: "var(--text-primary)", letterSpacing: "0.08em" }}>Account Email</p>
        {accounts.map(acc => (
          <div key={acc.id} className="sidebar-account-row">
            <button data-testid={`account-${acc.id}`} className={`sidebar-item ${activeAccount === acc.id ? "active" : ""}`}
              onClick={() => { setActiveAccount(activeAccount === acc.id ? null : acc.id); setActiveView("all"); setActiveTag(null); }}>
              <div className="sidebar-account-dot" style={{ background: acc.color || "#94A3B8" }} />
              {acc.type === "pec" ? <ShieldCheck size={16} weight="duotone" style={{ color: activeAccount === acc.id ? "var(--text-inverse)" : (acc.color || "var(--text-secondary)") }} /> : <Buildings size={16} style={{ color: activeAccount === acc.id ? "var(--text-inverse)" : (acc.color || "var(--text-secondary)") }} />}
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>{acc.name}</span>
              {acc.email_count != null && <span className="sidebar-item-count">{acc.email_count}</span>}
            </button>
            {onSyncAccount && (
              <button className={`sidebar-sync-btn ${syncingAccountId === acc.id ? "syncing" : ""}`}
                onClick={(e) => { e.stopPropagation(); onSyncAccount(acc.id); }}
                disabled={syncingAccountId === acc.id}
                data-testid={`sidebar-sync-${acc.id}`}
                title="Sincronizza ora">
                <ArrowsClockwise size={14} className={syncingAccountId === acc.id ? "spinning" : ""} />
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="sidebar-section" style={{ maxHeight: "200px", overflowY: "auto" }}>
        <p className="sidebar-section-title">Pratiche / Etichette</p>
        {tags.map(tag => (
          <button key={tag.id} data-testid={`tag-${tag.id}`} className={`sidebar-item ${activeTag === tag.id ? "active" : ""}`}
            onClick={() => { setActiveTag(activeTag === tag.id ? null : tag.id); setActiveView("all"); setActiveAccount(null); }}>
            <div className="sidebar-tag-dot" style={{ background: tag.color }} />
            {tag.name}
          </button>
        ))}
      </div>

      <div className="sidebar-footer">
        <div className="sidebar-footer-avatar">{getInitials(user?.name)}</div>
        <div className="sidebar-footer-info">
          <div className="sidebar-footer-name">{user?.name}</div>
          <div className="sidebar-footer-email">{user?.email}</div>
        </div>
        <button className="sidebar-footer-logout" onClick={onLogout} data-testid="logout-btn" title="Esci">
          <SignOut size={18} />
        </button>
      </div>
      <div className="sidebar-telca">
        <TelcaBadge />
      </div>
    </div>
  );
}

// ---- EMAIL LIST ----
function EmailList({ emails, total, selectedId, onSelect, searchQuery, setSearchQuery, folder, setFolder, dateFrom, setDateFrom, dateTo, setDateTo, loading, tags, activeAccount, accounts, selectedEmailIds, toggleSelectEmail, selectAllEmails, handleDeleteSelected }) {
  const tagMap = {};
  tags.forEach(t => { tagMap[t.id] = t; });
  const [showFilters, setShowFilters] = useState(false);
  const hasFilters = dateFrom || dateTo;
  const activeAcc = accounts?.find(a => a.id === activeAccount);

  const MONTHS_IT = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];

  const setMonthFilter = (year, month) => {
    const from = `${year}-${String(month).padStart(2, "0")}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const to = `${year}-${String(month).padStart(2, "0")}-${lastDay}`;
    setDateFrom(from);
    setDateTo(to);
  };

  const clearDateFilters = () => { setDateFrom(""); setDateTo(""); };

  return (
    <div className="email-list-pane" data-testid="email-list-pane">
      <div className="email-list-header">
        <div className="email-list-title">
          <EnvelopeSimple size={20} weight="duotone" />
          {activeAcc ? activeAcc.name : "Posta"}
          <span className="email-list-title-count">{total} email</span>
        </div>
        <div className="search-bar">
          <MagnifyingGlass size={16} className="search-bar-icon" />
          <input data-testid="search-input" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Cerca email, allegati o mittenti..." />
        </div>
      </div>

      <div className="folder-tabs">
        <button data-testid="folder-all" className={`folder-tab ${folder === "" ? "active" : ""}`} onClick={() => setFolder("")}>
          Tutte
        </button>
        <button data-testid="folder-inbox" className={`folder-tab ${folder === "inbox" ? "active" : ""}`} onClick={() => setFolder("inbox")}>
          <Tray size={14} style={{ marginRight: 4 }} /> Ricevute
        </button>
        <button data-testid="folder-sent" className={`folder-tab ${folder === "sent" ? "active" : ""}`} onClick={() => setFolder("sent")}>
          <PaperPlaneTilt size={14} style={{ marginRight: 4 }} /> Inviate
        </button>
        <div style={{ marginLeft: "auto" }}>
          <button className={`folder-tab ${showFilters ? "active" : ""} ${hasFilters ? "active" : ""}`}
            onClick={() => setShowFilters(!showFilters)} data-testid="toggle-date-filter">
            <CalendarBlank size={14} style={{ marginRight: 4 }} /> Periodo
            {hasFilters && <span style={{ marginLeft: 4, fontSize: "0.5625rem", background: "var(--accent-pec)", color: "#fff", borderRadius: 2, padding: "0 4px" }}>ON</span>}
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="date-filters animate-in" data-testid="date-filters">
          <div className="date-filter-row">
            <div className="date-filter-field">
              <label className="date-filter-label">Da:</label>
              <input type="date" className="date-filter-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="date-from-input" />
            </div>
            <div className="date-filter-field">
              <label className="date-filter-label">A:</label>
              <input type="date" className="date-filter-input" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="date-to-input" />
            </div>
            {hasFilters && (
              <button className="date-filter-clear" onClick={clearDateFilters} data-testid="clear-date-filter" title="Cancella filtro date">
                <X size={14} /> Cancella
              </button>
            )}
          </div>
          <div className="date-quick-filters">
            <span className="date-quick-label">Mese rapido:</span>
            {[0, 1, 2, 3, 4, 5].map(offset => {
              const d = new Date();
              d.setMonth(d.getMonth() - offset);
              const y = d.getFullYear();
              const m = d.getMonth() + 1;
              const isActive = dateFrom === `${y}-${String(m).padStart(2, "0")}-01`;
              return (
                <button key={offset} className={`date-quick-btn ${isActive ? "active" : ""}`}
                  onClick={() => setMonthFilter(y, m)} data-testid={`month-btn-${y}-${m}`}>
                  {MONTHS_IT[m - 1].slice(0, 3)} {y}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Selection toolbar */}
      {selectedEmailIds && selectedEmailIds.length > 0 && (
        <div className="email-selection-toolbar" data-testid="selection-toolbar">
          <label className="select-all-check">
            <input type="checkbox" checked={selectedEmailIds.length === emails.length && emails.length > 0}
              onChange={selectAllEmails} data-testid="select-all-checkbox" />
            <span>{selectedEmailIds.length} selezionate</span>
          </label>
          <button className="selection-delete-btn" onClick={handleDeleteSelected} data-testid="delete-selected-btn">
            <Trash size={14} /> Elimina ({selectedEmailIds.length})
          </button>
        </div>
      )}

      <div className="email-list-scroll" data-testid="email-list-scroll">
        {loading && <div style={{ padding: 24, textAlign: "center", color: "var(--text-tertiary)", fontSize: "0.8125rem" }}>Caricamento...</div>}
        {!loading && emails.length === 0 && (
          <div style={{ padding: 48, textAlign: "center", color: "var(--text-tertiary)", fontSize: "0.8125rem" }}>
            <FolderOpen size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
            <div>Nessuna email trovata</div>
          </div>
        )}
        {emails.map(em => (
          <div key={em.id} data-testid={`email-item-${em.id}`}
            className={`email-item ${selectedId === em.id ? "active" : ""} ${!em.read ? "unread" : ""} ${selectedEmailIds?.includes(em.id) ? "selected" : ""} animate-in`}
            onClick={() => onSelect(em.id)}>
            <div className="email-item-row1">
              <input type="checkbox" className="email-checkbox" checked={selectedEmailIds?.includes(em.id) || false}
                onChange={(e) => { e.stopPropagation(); toggleSelectEmail(em.id); }}
                onClick={(e) => e.stopPropagation()}
                data-testid={`checkbox-${em.id}`} />
              {!em.read && <div className="unread-dot" />}
              <span className="email-item-sender">{em.folder === "sent" ? em.to_name || em.to_email : em.from_name}</span>
              <span className="email-item-date">{formatDate(em.date)}</span>
            </div>
            <div className="email-item-subject">{em.subject}</div>
            <div className="email-item-row3">
              <span className={`email-type-badge ${em.account_type === "pec" ? "badge-pec" : "badge-exchange"}`}>
                {em.account_type === "pec" ? "PEC" : "365"}
              </span>
              {em.attachments?.length > 0 && (
                <span className="email-item-attachment-icon"><Paperclip size={12} /></span>
              )}
              <div className="email-item-tags">
                {(em.tags || []).slice(0, 2).map(tid => {
                  const t = tagMap[tid];
                  return t ? <span key={tid} className="email-item-tag" style={{ background: t.color }}>{t.name}</span> : null;
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- EMAIL DETAIL ----
function EmailDetail({ emailId, tags, allTags, onTagAdd, onTagRemove, onEmailSent, onTagCreated }) {
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTagMenu, setShowTagMenu] = useState(false);
  const [replyMode, setReplyMode] = useState(null); // null | "reply" | "replyAll" | "forward"
  const [replyForm, setReplyForm] = useState({ account_id: "", to_email: "", cc: "", subject: "", body: "" });
  const [accounts, setAccounts] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [creatingTag, setCreatingTag] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState("#2563EB");
  const QUICK_COLORS = ["#2563EB","#DC2626","#D97706","#059669","#7C3AED","#DB2777","#0891B2","#4F46E5"];

  const handleCreateAndAssignTag = async () => {
    if (!newTagName.trim()) return;
    try {
      const { data } = await ax.post("/tags", { name: newTagName.trim(), color: newTagColor });
      // Assign to current email
      await ax.post(`/emails/${email.id}/tags`, { tag_id: data.id });
      setEmail(prev => ({ ...prev, tags: [...(prev.tags || []), data.id] }));
      setCreatingTag(false);
      setNewTagName("");
      setNewTagColor("#2563EB");
      setShowTagMenu(false);
      if (onTagCreated) onTagCreated();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (!emailId) { setEmail(null); setReplyMode(null); return; }
    setLoading(true);
    setReplyMode(null);
    setSendResult(null);
    ax.get(`/emails/${emailId}`).then(r => setEmail(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [emailId]);

  const tagMap = {};
  allTags.forEach(t => { tagMap[t.id] = t; });

  const startReply = async (mode) => {
    if (accounts.length === 0) {
      try { const { data } = await ax.get("/compose/accounts"); setAccounts(data); } catch {}
    }
    setReplyMode(mode);
    setSendResult(null);
    const e = email;
    let to = "", cc = "", subj = "", body = "";
    const quotedBody = `\n\n--- Messaggio originale ---\nDa: ${e.from_name} <${e.from_email}>\nData: ${formatFullDate(e.date)}\nOggetto: ${e.subject}\n\n${e.body}`;
    if (mode === "reply") {
      to = e.folder === "sent" ? e.to_email : e.from_email;
      subj = e.subject.startsWith("RE:") || e.subject.startsWith("Re:") ? e.subject : `RE: ${e.subject}`;
      body = quotedBody;
    } else if (mode === "replyAll") {
      to = e.folder === "sent" ? e.to_email : e.from_email;
      subj = e.subject.startsWith("RE:") || e.subject.startsWith("Re:") ? e.subject : `RE: ${e.subject}`;
      body = quotedBody;
    } else if (mode === "forward") {
      to = "";
      subj = e.subject.startsWith("FW:") || e.subject.startsWith("Fwd:") ? e.subject : `FW: ${e.subject}`;
      body = `\n\n--- Messaggio inoltrato ---\nDa: ${e.from_name} <${e.from_email}>\nA: ${e.to_name} <${e.to_email}>\nData: ${formatFullDate(e.date)}\nOggetto: ${e.subject}\n\n${e.body}`;
    }
    // Auto-select the account that received/sent this email
    let accId = "";
    if (accounts.length > 0) {
      const match = accounts.find(a => a.can_send && (a.email === e.to_email || a.email === e.from_email));
      if (match) accId = match.id;
      else { const first = accounts.find(a => a.can_send); if (first) accId = first.id; }
    }
    setReplyForm({ account_id: accId, to_email: to, cc, subject: subj, body });
  };

  const handleSendReply = async () => {
    setSending(true); setSendResult(null);
    try {
      const { data } = await ax.post("/emails/send", {
        account_id: replyForm.account_id, to_email: replyForm.to_email,
        subject: replyForm.subject, body: replyForm.body, cc: replyForm.cc,
      });
      setSendResult(data);
      if (data.success) {
        setTimeout(() => { setReplyMode(null); setSendResult(null); if (onEmailSent) onEmailSent(); }, 2000);
      }
    } catch (e) {
      setSendResult({ success: false, message: formatApiError(e.response?.data?.detail) });
    } finally { setSending(false); }
  };

  if (!emailId) {
    return (
      <div className="detail-pane" data-testid="detail-pane-empty">
        <div className="detail-empty">
          <EnvelopeOpen size={48} className="detail-empty-icon" />
          <span className="detail-empty-text">Seleziona un'email per visualizzarla</span>
        </div>
      </div>
    );
  }

  if (loading || !email) {
    return (
      <div className="detail-pane" data-testid="detail-pane-loading">
        <div className="detail-empty">
          <span className="detail-empty-text">Caricamento...</span>
        </div>
      </div>
    );
  }

  const availableTags = allTags.filter(t => !(email.tags || []).includes(t.id));

  return (
    <div className="detail-pane animate-in" data-testid="detail-pane">
      <div className="detail-header">
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <h2 className="detail-subject" data-testid="detail-subject" style={{ flex: 1 }}>{email.subject}</h2>
          <div className="detail-actions" data-testid="detail-actions">
            <button className="detail-action-btn" onClick={() => startReply("reply")} data-testid="reply-btn" title="Rispondi">
              <ArrowLeft size={15} /> Rispondi
            </button>
            <button className="detail-action-btn" onClick={() => startReply("replyAll")} data-testid="reply-all-btn" title="Rispondi a tutti">
              <ArrowLeft size={15} /><ArrowLeft size={15} style={{ marginLeft: -8 }} /> Tutti
            </button>
            <button className="detail-action-btn" onClick={() => startReply("forward")} data-testid="forward-btn" title="Inoltra">
              <PaperPlaneTilt size={15} /> Inoltra
            </button>
          </div>
        </div>
        <div className="detail-meta">
          <div className="detail-meta-row">
            <span className="detail-meta-label">Da:</span>
            <span className="detail-meta-value">{email.from_name} &lt;{email.from_email}&gt;</span>
            <span className={`email-type-badge ${email.account_type === "pec" ? "badge-pec" : "badge-exchange"}`}>
              {email.account_type === "pec" ? "PEC" : email.account_type === "import" ? "IMPORT" : "Exchange 365"}
            </span>
            <span className="detail-meta-date">{formatFullDate(email.date)}</span>
          </div>
          <div className="detail-meta-row">
            <span className="detail-meta-label">A:</span>
            <span className="detail-meta-value">{email.to_name} &lt;{email.to_email}&gt;</span>
          </div>
        </div>
      </div>

      <div className="detail-tags" data-testid="detail-tags">
        <Tag size={14} style={{ color: "var(--text-tertiary)" }} />
        {(email.tags || []).map(tid => {
          const t = tagMap[tid];
          if (!t) return null;
          return (
            <span key={tid} className="detail-tag" style={{ background: t.color }}>
              {t.name}
              <span className="detail-tag-remove" onClick={() => onTagRemove(email.id, tid)} data-testid={`remove-tag-${tid}`}>
                <X size={10} />
              </span>
            </span>
          );
        })}
        <div className="tag-dropdown">
          <button className="detail-add-tag-btn" onClick={() => setShowTagMenu(!showTagMenu)} data-testid="add-tag-btn">
            <Plus size={12} /> Aggiungi
          </button>
          {showTagMenu && (
            <div className="tag-dropdown-menu" data-testid="tag-dropdown-menu">
              {!creatingTag && (
                <>
                  {availableTags.length === 0 && <div style={{ padding: "8px 12px", fontSize: "0.75rem", color: "var(--text-tertiary)" }}>Nessun tag disponibile</div>}
                  {availableTags.map(t => (
                    <button key={t.id} className="tag-dropdown-item" data-testid={`assign-tag-${t.id}`}
                      onClick={() => { onTagAdd(email.id, t.id); setShowTagMenu(false); setEmail(prev => ({ ...prev, tags: [...(prev.tags || []), t.id] })); }}>
                      <div className="sidebar-tag-dot" style={{ background: t.color }} />
                      {t.name}
                    </button>
                  ))}
                  <div style={{ borderTop: "1px solid var(--border-default)", margin: "4px 0" }} />
                  <button className="tag-dropdown-item" style={{ color: "var(--accent-pec)", fontWeight: 600 }}
                    onClick={() => setCreatingTag(true)} data-testid="create-tag-inline-btn">
                    <Plus size={14} /> Crea nuova etichetta...
                  </button>
                </>
              )}
              {creatingTag && (
                <div className="tag-create-inline" data-testid="tag-create-inline">
                  <input className="tag-inline-input" value={newTagName} onChange={e => setNewTagName(e.target.value)}
                    placeholder="Nome etichetta..." autoFocus data-testid="tag-inline-name"
                    onKeyDown={e => { if (e.key === "Enter") handleCreateAndAssignTag(); if (e.key === "Escape") { setCreatingTag(false); setNewTagName(""); }}} />
                  <div className="tag-inline-colors">
                    {QUICK_COLORS.map(c => (
                      <button key={c} className={`tag-inline-color ${newTagColor === c ? "active" : ""}`}
                        style={{ background: c }} onClick={() => setNewTagColor(c)} />
                    ))}
                  </div>
                  <div className="tag-inline-actions">
                    <button className="tag-inline-save" onClick={handleCreateAndAssignTag} disabled={!newTagName.trim()} data-testid="tag-inline-save">
                      <CheckCircle size={12} /> Crea e Assegna
                    </button>
                    <button className="tag-inline-cancel" onClick={() => { setCreatingTag(false); setNewTagName(""); }} data-testid="tag-inline-cancel">
                      <X size={12} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reply / Forward inline form */}
      {replyMode && (
        <div className="reply-form animate-in" data-testid="reply-form">
          <div className="reply-form-header">
            <span className="reply-form-title">
              {replyMode === "reply" ? "Rispondi" : replyMode === "replyAll" ? "Rispondi a Tutti" : "Inoltra"}
            </span>
            <button className="reply-form-close" onClick={() => { setReplyMode(null); setSendResult(null); }} data-testid="reply-close-btn">
              <X size={16} />
            </button>
          </div>
          <div className="reply-form-fields">
            <div className="reply-field-row">
              <label className="reply-label">Da:</label>
              <select className="reply-select" value={replyForm.account_id} onChange={e => setReplyForm(p => ({ ...p, account_id: e.target.value }))} data-testid="reply-from-select">
                <option value="">— Seleziona —</option>
                {accounts.map(a => <option key={a.id} value={a.id} disabled={!a.can_send}>{a.name} ({a.email}){!a.can_send ? " — SMTP non configurato" : ""}</option>)}
              </select>
            </div>
            <div className="reply-field-row">
              <label className="reply-label">A:</label>
              <input className="reply-input" value={replyForm.to_email} onChange={e => setReplyForm(p => ({ ...p, to_email: e.target.value }))} placeholder="destinatario@email.it" data-testid="reply-to-input" />
            </div>
            {replyMode === "replyAll" && (
              <div className="reply-field-row">
                <label className="reply-label">CC:</label>
                <input className="reply-input" value={replyForm.cc} onChange={e => setReplyForm(p => ({ ...p, cc: e.target.value }))} placeholder="copia@email.it" data-testid="reply-cc-input" />
              </div>
            )}
            <div className="reply-field-row">
              <label className="reply-label">Ogg:</label>
              <input className="reply-input" value={replyForm.subject} onChange={e => setReplyForm(p => ({ ...p, subject: e.target.value }))} data-testid="reply-subject-input" />
            </div>
            <textarea className="reply-textarea" value={replyForm.body} onChange={e => setReplyForm(p => ({ ...p, body: e.target.value }))} rows={8} data-testid="reply-body-input" />
          </div>
          {sendResult && (
            <div className={`config-result ${sendResult.success ? "config-result-ok" : "config-result-error"}`} style={{ margin: "8px 0" }} data-testid="reply-result">
              {sendResult.success ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
              {sendResult.message}
            </div>
          )}
          <div className="reply-form-actions">
            <button className="config-btn config-btn-save" onClick={handleSendReply}
              disabled={sending || !replyForm.account_id || !replyForm.to_email || !replyForm.subject}
              data-testid="reply-send-btn">
              {sending ? <><CircleNotch size={14} className="spinning" /> Invio...</> : <><PaperPlaneTilt size={14} /> Invia</>}
            </button>
            <button className="config-btn" onClick={() => { setReplyMode(null); setSendResult(null); }} data-testid="reply-cancel-btn">
              <X size={14} /> Annulla
            </button>
          </div>
        </div>
      )}

      <div className="detail-body" data-testid="detail-body">
        <div className="detail-body-text">{email.body}</div>
      </div>

      {email.attachments?.length > 0 && (
        <div className="detail-attachments" data-testid="detail-attachments">
          <p className="detail-attachments-title">
            <Paperclip size={12} style={{ marginRight: 4 }} />
            Allegati ({email.attachments.length})
          </p>
          <div className="detail-attachments-grid">
            {email.attachments.map(att => (
              <div key={att.id} className="attachment-item" data-testid={`attachment-${att.id}`}>
                <span className="attachment-icon">{getFileIcon(att.type)}</span>
                <span className="attachment-name">{att.name}</span>
                <span className="attachment-size">{formatSize(att.size)}</span>
                <DownloadSimple size={14} style={{ color: "var(--text-tertiary)" }} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- STATS VIEW ----
function StatsView({ stats, accounts }) {
  if (!stats) return null;
  const pecAccounts = accounts.filter(a => a.type === "pec");
  const exAccounts = accounts.filter(a => a.type === "exchange");

  return (
    <div className="detail-pane animate-in" data-testid="stats-view" style={{ overflow: "auto" }}>
      <div style={{ padding: "24px 24px 0" }}>
        <h2 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "1.5rem", fontWeight: 900, margin: "0 0 4px" }}>
          <ChartBar size={24} weight="duotone" style={{ marginRight: 8 }} />
          Statistiche Archivio
        </h2>
        <p style={{ color: "var(--text-tertiary)", fontSize: "0.8125rem", margin: 0 }}>Panoramica del sistema di archiviazione email</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <p className="stat-card-label">Email Totali</p>
          <p className="stat-card-value" data-testid="stat-total">{stats.total_emails}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card-label">Email PEC</p>
          <p className="stat-card-value" data-testid="stat-pec" style={{ color: "var(--accent-pec)" }}>{stats.total_pec}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card-label">Email Exchange</p>
          <p className="stat-card-value" data-testid="stat-exchange" style={{ color: "var(--accent-exchange)" }}>{stats.total_exchange}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card-label">Con Allegati</p>
          <p className="stat-card-value" data-testid="stat-attachments">{stats.total_attachments}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card-label">Non Lette</p>
          <p className="stat-card-value" data-testid="stat-unread" style={{ color: "var(--accent-warning)" }}>{stats.unread}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card-label">Account Configurati</p>
          <p className="stat-card-value" data-testid="stat-accounts">{stats.accounts}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card-label">Pratiche / Etichette</p>
          <p className="stat-card-value" data-testid="stat-tags">{stats.tags}</p>
        </div>
      </div>

      <div style={{ padding: "0 24px 24px" }}>
        <h3 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "1rem", fontWeight: 700, margin: "0 0 12px" }}>Account Configurati</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <p style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--text-tertiary)", marginBottom: 8 }}>
              <ShieldCheck size={12} weight="duotone" style={{ marginRight: 4 }} /> PEC ({pecAccounts.length})
            </p>
            {pecAccounts.map(a => (
              <div key={a.id} style={{ padding: "8px 12px", border: "1px solid var(--border-default)", borderRadius: 2, marginBottom: 6, fontSize: "0.8125rem" }}>
                <div style={{ fontWeight: 600 }}>{a.name}</div>
                <div style={{ color: "var(--text-tertiary)", fontSize: "0.75rem" }}>{a.email} — {a.provider}</div>
              </div>
            ))}
          </div>
          <div>
            <p style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--text-tertiary)", marginBottom: 8 }}>
              <Buildings size={12} style={{ marginRight: 4 }} /> Exchange 365 ({exAccounts.length})
            </p>
            {exAccounts.map(a => (
              <div key={a.id} style={{ padding: "8px 12px", border: "1px solid var(--border-default)", borderRadius: 2, marginBottom: 6, fontSize: "0.8125rem" }}>
                <div style={{ fontWeight: 600 }}>{a.name}</div>
                <div style={{ color: "var(--text-tertiary)", fontSize: "0.75rem" }}>{a.email} — {a.provider}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- CONFIG PANEL ----
function ConfigPanel({ onAccountsChanged }) {
  const [configAccounts, setConfigAccounts] = useState([]);
  const [presets, setPresets] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [form, setForm] = useState({
    name: "", email_address: "", account_type: "pec", provider: "legalmail",
    imap_server: "mbox.cert.legalmail.it", imap_port: 993, imap_username: "",
    imap_password: "", smtp_server: "sendm.cert.legalmail.it", smtp_port: 465,
    use_ssl: true, color: "#2563EB", sync_days: 90, sync_interval_minutes: 5,
  });

  const loadAccounts = useCallback(async () => {
    try {
      const [accRes, preRes] = await Promise.all([ax.get("/config/accounts"), ax.get("/config/presets")]);
      setConfigAccounts(accRes.data);
      setPresets(preRes.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const applyPreset = (provider) => {
    const p = presets[provider];
    if (!p) return;
    setForm(prev => ({
      ...prev, provider, imap_server: p.imap_server, imap_port: p.imap_port,
      smtp_server: p.smtp_server, smtp_port: p.smtp_port, use_ssl: p.use_ssl,
      account_type: (provider === "legalmail" || provider === "aruba_pec") ? "pec" : "exchange",
    }));
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await ax.post("/config/test-connection", {
        imap_server: form.imap_server, imap_port: form.imap_port,
        imap_username: form.imap_username || form.email_address,
        imap_password: form.imap_password, use_ssl: form.use_ssl,
      });
      setTestResult(data);
    } catch (e) {
      setTestResult({ success: false, message: formatApiError(e.response?.data?.detail) });
    } finally { setTesting(false); }
  };

  const handleSave = async () => {
    try {
      await ax.post("/config/accounts", {
        ...form, imap_username: form.imap_username || form.email_address, sync_enabled: true,
      });
      setShowForm(false); setTestResult(null);
      setForm({ name: "", email_address: "", account_type: "pec", provider: "legalmail", imap_server: "mbox.cert.legalmail.it", imap_port: 993, imap_username: "", imap_password: "", smtp_server: "sendm.cert.legalmail.it", smtp_port: 465, use_ssl: true, color: "#2563EB", sync_days: 90, sync_interval_minutes: 5 });
      loadAccounts();
      onAccountsChanged();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo account e tutte le email associate?")) return;
    await ax.delete(`/config/accounts/${id}`);
    loadAccounts(); onAccountsChanged();
  };

  const handleSync = async (id) => {
    setSyncing(id); setSyncResult(null);
    try {
      const { data } = await ax.post(`/config/accounts/${id}/sync`);
      setSyncResult({ id, ...data });
      loadAccounts(); onAccountsChanged();
    } catch (e) {
      setSyncResult({ id, success: false, message: formatApiError(e.response?.data?.detail) });
    } finally { setSyncing(null); }
  };

  const providerLabels = { legalmail: "Legalmail (PEC)", aruba_pec: "Aruba PEC", pecimprese: "PEC Imprese", ordineavvocativr: "Ordine Avvocati VR (PEC)", exchange_365: "Microsoft 365 Exchange", gmail: "Gmail", custom: "Server IMAP personalizzato" };

  return (
    <div className="detail-pane animate-in" data-testid="config-panel" style={{ overflow: "auto" }}>
      <div style={{ padding: "24px 24px 0" }}>
        <h2 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "1.5rem", fontWeight: 900, margin: "0 0 4px", display: "flex", alignItems: "center", gap: 10 }}>
          <Gear size={24} weight="duotone" />
          Configurazione Account Email
        </h2>
        <p style={{ color: "var(--text-tertiary)", fontSize: "0.8125rem", margin: "0 0 20px" }}>
          Collega le caselle email PEC e Exchange per sincronizzare automaticamente le email nell'archivio.
        </p>
      </div>

      {/* Account list */}
      <div style={{ padding: "0 24px" }}>
        {loading && <p style={{ color: "var(--text-tertiary)", fontSize: "0.8125rem" }}>Caricamento...</p>}

        {configAccounts.map(acc => (
          <div key={acc.id} className="config-account-card" data-testid={`config-account-${acc.id}`}>
            <div className="config-account-header">
              <div className="config-account-dot" style={{ background: acc.color }} />
              <div style={{ flex: 1 }}>
                <div className="config-account-name">{acc.name}</div>
                <div className="config-account-email">{acc.email_address} — {providerLabels[acc.provider] || acc.provider}</div>
              </div>
              <span className={`config-status-badge ${acc.status === "synced" ? "status-ok" : acc.status === "error" ? "status-error" : "status-pending"}`}>
                {acc.status === "synced" ? "Sincronizzato" : acc.status === "error" ? "Errore" : "Configurato"}
              </span>
            </div>
            <div className="config-account-info">
              <span>Server: {acc.imap_server}:{acc.imap_port}</span>
              <span>Tipo: {acc.account_type.toUpperCase()}</span>
              <span>Sync: ogni {acc.sync_interval_minutes || 5} min</span>
              {acc.last_sync && <span>Ultima: {formatDate(acc.last_sync)}</span>}
              {acc.emails_synced > 0 && <span>{acc.emails_synced} email</span>}
            </div>
            <div className="config-account-actions">
              <button className="config-btn config-btn-sync" onClick={() => handleSync(acc.id)} disabled={syncing === acc.id} data-testid={`sync-btn-${acc.id}`}>
                {syncing === acc.id ? <><CircleNotch size={14} className="spinning" /> Sincronizzazione...</> : <><ArrowsClockwise size={14} /> Sincronizza Ora</>}
              </button>
              <button className="config-btn config-btn-delete" onClick={() => handleDelete(acc.id)} data-testid={`delete-btn-${acc.id}`}>
                <Trash size={14} /> Elimina
              </button>
            </div>
            {syncResult && syncResult.id === acc.id && (
              <div className={`config-result ${syncResult.success ? "config-result-ok" : "config-result-error"}`}>
                {syncResult.success ? <CheckCircle size={16} weight="fill" /> : <XCircle size={16} weight="fill" />}
                {syncResult.message}
              </div>
            )}
          </div>
        ))}

        {configAccounts.length === 0 && !loading && !showForm && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "var(--text-tertiary)" }}>
            <Plug size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
            <p style={{ fontSize: "0.875rem", margin: "0 0 4px" }}>Nessun account configurato</p>
            <p style={{ fontSize: "0.75rem" }}>Aggiungi il primo account email per iniziare a sincronizzare.</p>
          </div>
        )}
      </div>

      {/* Add account button */}
      {!showForm && (
        <div style={{ padding: "16px 24px" }}>
          <button className="config-add-btn" onClick={() => setShowForm(true)} data-testid="add-account-btn">
            <Plus size={16} /> Aggiungi Account Email
          </button>
        </div>
      )}

      {/* Add account form */}
      {showForm && (
        <div className="config-form" data-testid="config-form">
          <h3 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "1rem", fontWeight: 700, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}>
            <Plus size={18} /> Nuovo Account Email
          </h3>

          <div className="config-form-section">
            <label className="config-label">Provider</label>
            <div className="config-provider-grid">
              {Object.entries(providerLabels).map(([key, label]) => (
                <button key={key} className={`config-provider-btn ${form.provider === key ? "active" : ""}`}
                  onClick={() => applyPreset(key)} data-testid={`provider-${key}`}>
                  {key.includes("pec") || key === "legalmail" ? <ShieldCheck size={16} weight="duotone" /> : <Buildings size={16} />}
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="config-form-row">
            <div className="config-form-field">
              <label className="config-label">Nome Account</label>
              <input className="config-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="es. PEC Studio Zilioli" data-testid="config-name-input" />
            </div>
            <div className="config-form-field">
              <label className="config-label">Indirizzo Email</label>
              <input className="config-input" type="email" value={form.email_address} onChange={e => setForm(p => ({ ...p, email_address: e.target.value }))} placeholder="es. studio@legalmail.it" data-testid="config-email-input" />
            </div>
          </div>

          <div className="config-form-row">
            <div className="config-form-field">
              <label className="config-label">Server IMAP</label>
              <input className="config-input" value={form.imap_server} onChange={e => setForm(p => ({ ...p, imap_server: e.target.value }))} data-testid="config-imap-server" />
            </div>
            <div className="config-form-field" style={{ maxWidth: 100 }}>
              <label className="config-label">Porta</label>
              <input className="config-input" type="number" value={form.imap_port} onChange={e => setForm(p => ({ ...p, imap_port: parseInt(e.target.value) || 993 }))} data-testid="config-imap-port" />
            </div>
          </div>

          <div className="config-form-row">
            <div className="config-form-field">
              <label className="config-label">Username IMAP (se diverso dall'email)</label>
              <input className="config-input" value={form.imap_username} onChange={e => setForm(p => ({ ...p, imap_username: e.target.value }))} placeholder={form.email_address || "Lascia vuoto per usare l'email"} data-testid="config-imap-username" />
            </div>
            <div className="config-form-field">
              <label className="config-label">Password</label>
              <div className="password-input-wrapper">
                <input className="config-input" type={showPwd ? "text" : "password"} value={form.imap_password} onChange={e => setForm(p => ({ ...p, imap_password: e.target.value }))} placeholder="Password della casella" data-testid="config-imap-password" />
                <button type="button" className="password-toggle" onClick={() => setShowPwd(!showPwd)} data-testid="config-pwd-toggle">
                  {showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          </div>

          <div className="config-form-row">
            <div className="config-form-field">
              <label className="config-label">Server SMTP (per invio)</label>
              <input className="config-input" value={form.smtp_server} onChange={e => setForm(p => ({ ...p, smtp_server: e.target.value }))} data-testid="config-smtp-server" />
            </div>
            <div className="config-form-field" style={{ maxWidth: 100 }}>
              <label className="config-label">Porta</label>
              <input className="config-input" type="number" value={form.smtp_port} onChange={e => setForm(p => ({ ...p, smtp_port: parseInt(e.target.value) || 465 }))} data-testid="config-smtp-port" />
            </div>
            <div className="config-form-field" style={{ maxWidth: 120 }}>
              <label className="config-label">Giorni da sync</label>
              <input className="config-input" type="number" value={form.sync_days} onChange={e => setForm(p => ({ ...p, sync_days: parseInt(e.target.value) || 90 }))} data-testid="config-sync-days" />
            </div>
            <div className="config-form-field" style={{ maxWidth: 150 }}>
              <label className="config-label">Intervallo sync (min)</label>
              <input className="config-input" type="number" min="2" max="60" value={form.sync_interval_minutes} onChange={e => setForm(p => ({ ...p, sync_interval_minutes: Math.max(2, parseInt(e.target.value) || 5) }))} data-testid="config-sync-interval" />
            </div>
          </div>

          <div className="config-form-row">
            <div className="config-form-field" style={{ maxWidth: 100 }}>
              <label className="config-label">Colore</label>
              <input type="color" value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} style={{ width: "100%", height: 36, border: "1px solid var(--border-default)", borderRadius: 2, cursor: "pointer" }} data-testid="config-color" />
            </div>
          </div>

          {testResult && (
            <div className={`config-result ${testResult.success ? "config-result-ok" : "config-result-error"}`} data-testid="test-result">
              {testResult.success ? <CheckCircle size={16} weight="fill" /> : <XCircle size={16} weight="fill" />}
              {testResult.message}
            </div>
          )}

          <div className="config-form-actions">
            <button className="config-btn config-btn-test" onClick={handleTest} disabled={testing || !form.imap_server || !form.imap_password} data-testid="test-connection-btn">
              {testing ? <><CircleNotch size={14} className="spinning" /> Test in corso...</> : <><TestTube size={14} /> Testa Connessione</>}
            </button>
            <button className="config-btn config-btn-save" onClick={handleSave} disabled={!form.name || !form.email_address || !form.imap_password} data-testid="save-account-btn">
              <CheckCircle size={14} /> Salva Account
            </button>
            <button className="config-btn config-btn-cancel" onClick={() => { setShowForm(false); setTestResult(null); }} data-testid="cancel-add-btn">
              <X size={14} /> Annulla
            </button>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div style={{ padding: "16px 24px 24px" }}>
        <div className="config-help">
          <h4 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "0.8125rem", fontWeight: 700, margin: "0 0 8px" }}>
            <Warning size={14} style={{ marginRight: 4 }} /> Guida rapida
          </h4>
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: "0.75rem", color: "var(--text-secondary)", lineHeight: 1.8 }}>
            <li><strong>PEC Legalmail:</strong> Server IMAP: mbox.cert.legalmail.it — Porta: 993 — Username: indirizzo PEC completo</li>
            <li><strong>PEC Aruba:</strong> Server IMAP: imaps.pec.aruba.it — Porta: 993 — Username: indirizzo PEC completo</li>
            <li><strong>PEC Imprese:</strong> Server IMAP: imaps.pecimprese.it — Porta: 993 — Username: indirizzo PEC completo</li>
            <li><strong>Ordine Avvocati VR:</strong> Server IMAP: imaps.sicurezzapostale.it — Porta: 993 — Username: indirizzo PEC completo (provider: Sicurezza Postale/Namirial)</li>
            <li><strong>Exchange 365:</strong> Server IMAP: outlook.office365.com — Porta: 993 — Username: indirizzo email completo</li>
            <li>Usa il pulsante <strong>"Testa Connessione"</strong> per verificare le credenziali prima di salvare</li>
            <li>Dopo il salvataggio, clicca <strong>"Sincronizza Ora"</strong> per scaricare le email</li>
            <li><strong>Sincronizzazione automatica:</strong> una volta configurato, le email vengono scaricate automaticamente in base all'intervallo impostato (default: 5 min)</li>
            <li><strong>Consiglio:</strong> per caselle PEC (Legalmail/Aruba) usare minimo 5 minuti per evitare blocchi dal provider</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ---- SIGNATURES PANEL ----
function SignaturesPanel() {
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSig, setEditingSig] = useState(null);
  const [form, setForm] = useState({ name: "", html_content: "", is_default: false, account_id: null });
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState("");

  const loadSignatures = useCallback(async () => {
    try {
      const [sigRes, accRes] = await Promise.all([ax.get("/signatures"), ax.get("/compose/accounts")]);
      setSignatures(sigRes.data);
      setAccounts(accRes.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSignatures(); }, [loadSignatures]);
  const resetForm = () => { setForm({ name: "", html_content: "", is_default: false, account_id: null }); setError(""); };

  const handleSave = async () => {
    if (!form.name.trim() || !form.html_content.trim()) { setError("Nome e contenuto HTML sono obbligatori"); return; }
    setError("");
    try {
      if (editingSig) {
        await ax.put(`/signatures/${editingSig.id}`, form);
      } else {
        await ax.post("/signatures", form);
      }
      setShowForm(false); setEditingSig(null); resetForm(); loadSignatures();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Eliminare la firma "${name}"?`)) return;
    await ax.delete(`/signatures/${id}`);
    loadSignatures();
  };

  const handleSetDefault = async (sig) => {
    await ax.put(`/signatures/${sig.id}`, { is_default: !sig.is_default });
    loadSignatures();
  };

  const startEdit = (sig) => {
    setEditingSig(sig);
    setForm({ name: sig.name, html_content: sig.html_content, is_default: sig.is_default, account_id: sig.account_id });
    setShowForm(false); setError("");
  };

  return (
    <div className="detail-pane animate-in" data-testid="signatures-panel" style={{ overflow: "auto" }}>
      <div style={{ padding: "24px 24px 0" }}>
        <h2 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "1.5rem", fontWeight: 900, margin: "0 0 4px", display: "flex", alignItems: "center", gap: 10 }}>
          <Signature size={24} weight="duotone" />
          Gestione Firme Email
        </h2>
        <p style={{ color: "var(--text-tertiary)", fontSize: "0.8125rem", margin: "0 0 20px" }}>
          Crea firme professionali da allegare automaticamente alle email. Puoi incollare il codice HTML generato da Wisestamp o altri servizi.
        </p>
      </div>

      <div style={{ padding: "0 24px" }}>
        {loading && <p style={{ color: "var(--text-tertiary)", fontSize: "0.8125rem" }}>Caricamento...</p>}

        {signatures.map(sig => (
          <div key={sig.id} className="sig-card" data-testid={`sig-card-${sig.id}`}>
            <div className="sig-card-header">
              <div style={{ flex: 1 }}>
                <div className="sig-card-name">
                  {sig.name}
                  {sig.is_default && <span className="sig-default-badge">PREDEFINITA</span>}
                </div>
                <div style={{ fontSize: "0.6875rem", color: "var(--text-tertiary)" }}>
                  {sig.account_id ? `Account: ${sig.account_id}` : "Globale (tutti gli account)"}
                </div>
              </div>
              <div className="user-card-actions">
                <button className="config-btn" onClick={() => handleSetDefault(sig)} data-testid={`default-sig-${sig.id}`}
                  title={sig.is_default ? "Rimuovi come predefinita" : "Imposta come predefinita"}>
                  <StarIcon size={14} weight={sig.is_default ? "fill" : "regular"} style={{ color: sig.is_default ? "#D97706" : undefined }} />
                  {sig.is_default ? "Predefinita" : "Imposta"}
                </button>
                <button className="config-btn" onClick={() => startEdit(sig)} data-testid={`edit-sig-${sig.id}`}>
                  <PencilSimple size={14} /> Modifica
                </button>
                <button className="config-btn config-btn-delete" onClick={() => handleDelete(sig.id, sig.name)} data-testid={`delete-sig-${sig.id}`}>
                  <Trash size={14} />
                </button>
              </div>
            </div>
            <div className="sig-preview" data-testid={`sig-preview-${sig.id}`}>
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sig.html_content || "") }} />
            </div>
          </div>
        ))}

        {signatures.length === 0 && !loading && !showForm && !editingSig && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "var(--text-tertiary)" }}>
            <Signature size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
            <p style={{ fontSize: "0.875rem", margin: "0 0 4px" }}>Nessuna firma creata</p>
            <p style={{ fontSize: "0.75rem" }}>Crea una firma professionale per le tue email.</p>
          </div>
        )}
      </div>

      {!showForm && !editingSig && (
        <div style={{ padding: "16px 24px" }}>
          <button className="config-add-btn" onClick={() => { setShowForm(true); resetForm(); }} data-testid="add-signature-btn">
            <Plus size={16} /> Nuova Firma Email
          </button>
        </div>
      )}

      {(showForm || editingSig) && (
        <div className="config-form" style={{ margin: "0 24px" }} data-testid="signature-form">
          <h3 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "1rem", fontWeight: 700, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}>
            {editingSig ? <><PencilSimple size={18} /> Modifica Firma</> : <><Plus size={18} /> Nuova Firma</>}
          </h3>

          {error && <div className="config-result config-result-error" style={{ marginBottom: 12 }}><XCircle size={16} weight="fill" /> {error}</div>}

          <div className="config-form-row">
            <div className="config-form-field">
              <label className="config-label">Nome Firma</label>
              <input className="config-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="es. Firma Avv. Zilioli, Firma Studio" data-testid="sig-name-input" />
            </div>
            <div className="config-form-field" style={{ maxWidth: 250 }}>
              <label className="config-label">Associa ad Account (opzionale)</label>
              <select className="config-input" value={form.account_id || ""} onChange={e => setForm(p => ({ ...p, account_id: e.target.value || null }))} data-testid="sig-account-select">
                <option value="">Globale (tutti gli account)</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.email})</option>)}
              </select>
            </div>
          </div>

          <div className="config-form-section">
            <label className="config-label">
              <Code size={12} style={{ marginRight: 4 }} />
              Codice HTML della Firma (incolla da Wisestamp o altro)
            </label>
            <textarea className="config-input sig-html-textarea" value={form.html_content}
              onChange={e => setForm(p => ({ ...p, html_content: e.target.value }))}
              placeholder={"Incolla qui il codice HTML della firma...\n\nPuoi generarlo su wisestamp.com o crearlo manualmente."}
              rows={10} data-testid="sig-html-input" />
          </div>

          {form.html_content && (
            <div className="config-form-section">
              <label className="config-label">Anteprima Firma</label>
              <div className="sig-preview" data-testid="sig-preview-form">
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(form.html_content || "") }} />
              </div>
            </div>
          )}

          <div className="config-form-row" style={{ alignItems: "center" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.8125rem", cursor: "pointer" }}>
              <input type="checkbox" checked={form.is_default} onChange={e => setForm(p => ({ ...p, is_default: e.target.checked }))} data-testid="sig-default-check" />
              Imposta come firma predefinita
            </label>
          </div>

          <div className="config-form-actions">
            <button className="config-btn config-btn-save" onClick={handleSave} disabled={!form.name.trim() || !form.html_content.trim()} data-testid="save-signature-btn">
              <CheckCircle size={14} /> {editingSig ? "Salva Modifiche" : "Crea Firma"}
            </button>
            <button className="config-btn config-btn-cancel" onClick={() => { setShowForm(false); setEditingSig(null); resetForm(); }} data-testid="cancel-signature-btn">
              <X size={14} /> Annulla
            </button>
          </div>
        </div>
      )}

      <div style={{ padding: "16px 24px 24px" }}>
        <div className="config-help">
          <h4 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "0.8125rem", fontWeight: 700, margin: "0 0 8px" }}>
            <Warning size={14} style={{ marginRight: 4 }} /> Come creare una firma
          </h4>
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: "0.75rem", color: "var(--text-secondary)", lineHeight: 1.8 }}>
            <li>Vai su <strong>wisestamp.com</strong> (o altro generatore di firme email)</li>
            <li>Crea la tua firma con logo, dati, link social</li>
            <li>Copia il <strong>codice HTML</strong> generato</li>
            <li>Incollalo nel campo "Codice HTML della Firma" qui sopra</li>
            <li>La firma verra' automaticamente allegata alle email inviate</li>
            <li>Puoi creare firme diverse per account diversi o una firma globale</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ---- COMPOSE EMAIL ----
function ComposePanel({ onEmailSent }) {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ account_id: "", to_email: "", to_name: "", subject: "", body: "", cc: "" });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [showCc, setShowCc] = useState(false);

  useEffect(() => {
    ax.get("/compose/accounts").then(r => {
      setAccounts(r.data);
      const sendable = r.data.find(a => a.can_send);
      if (sendable) setForm(p => ({ ...p, account_id: sendable.id }));
    }).catch(() => {});
  }, []);

  const handleSend = async () => {
    if (!form.account_id || !form.to_email || !form.subject) return;
    setSending(true); setResult(null);
    try {
      const { data } = await ax.post("/emails/send", form);
      setResult(data);
      if (data.success) {
        setTimeout(() => { setForm(p => ({ ...p, to_email: "", to_name: "", subject: "", body: "", cc: "" })); setResult(null); onEmailSent(); }, 2000);
      }
    } catch (e) {
      setResult({ success: false, message: formatApiError(e.response?.data?.detail) });
    } finally { setSending(false); }
  };

  const selectedAcc = accounts.find(a => a.id === form.account_id);

  return (
    <div className="detail-pane animate-in" data-testid="compose-panel" style={{ overflow: "auto" }}>
      <div style={{ padding: "24px 24px 0" }}>
        <h2 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "1.5rem", fontWeight: 900, margin: "0 0 4px", display: "flex", alignItems: "center", gap: 10 }}>
          <PaperPlaneIcon size={24} weight="duotone" />
          Componi Email
        </h2>
        <p style={{ color: "var(--text-tertiary)", fontSize: "0.8125rem", margin: "0 0 20px" }}>
          Invia email dalle caselle configurate nel sistema.
        </p>
      </div>

      <div className="config-form" style={{ margin: "0 24px" }} data-testid="compose-form">
        <div className="config-form-section">
          <label className="config-label">Da (Account Mittente)</label>
          <select className="config-input" value={form.account_id} onChange={e => setForm(p => ({ ...p, account_id: e.target.value }))} data-testid="compose-from-select">
            <option value="">— Seleziona account —</option>
            {accounts.map(a => (
              <option key={a.id} value={a.id} disabled={!a.can_send}>
                {a.name} ({a.email}) {!a.can_send ? " — SMTP non configurato" : ""}
              </option>
            ))}
          </select>
          {selectedAcc && !selectedAcc.can_send && (
            <p style={{ fontSize: "0.6875rem", color: "var(--accent-danger)", marginTop: 4 }}>
              Questo account non ha le credenziali SMTP configurate. Vai in Configurazione per aggiungerle.
            </p>
          )}
        </div>

        <div className="config-form-row">
          <div className="config-form-field">
            <label className="config-label">A (Destinatario)</label>
            <input className="config-input" type="email" value={form.to_email} onChange={e => setForm(p => ({ ...p, to_email: e.target.value }))} placeholder="destinatario@email.it" data-testid="compose-to-input" />
          </div>
          <div className="config-form-field" style={{ maxWidth: 200 }}>
            <label className="config-label">Nome Destinatario</label>
            <input className="config-input" value={form.to_name} onChange={e => setForm(p => ({ ...p, to_name: e.target.value }))} placeholder="Opzionale" data-testid="compose-to-name" />
          </div>
          {!showCc && (
            <button className="config-btn" style={{ alignSelf: "flex-end", marginBottom: 0 }} onClick={() => setShowCc(true)} data-testid="show-cc-btn">
              + CC
            </button>
          )}
        </div>

        {showCc && (
          <div className="config-form-row">
            <div className="config-form-field">
              <label className="config-label">CC</label>
              <input className="config-input" type="email" value={form.cc} onChange={e => setForm(p => ({ ...p, cc: e.target.value }))} placeholder="copia@email.it" data-testid="compose-cc-input" />
            </div>
          </div>
        )}

        <div className="config-form-section">
          <label className="config-label">Oggetto</label>
          <input className="config-input" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="Oggetto dell'email" data-testid="compose-subject-input" />
        </div>

        <div className="config-form-section">
          <label className="config-label">Messaggio</label>
          <textarea className="config-input compose-textarea" value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))} placeholder="Scrivi il tuo messaggio..." rows={12} data-testid="compose-body-input" />
        </div>

        {result && (
          <div className={`config-result ${result.success ? "config-result-ok" : "config-result-error"}`} data-testid="compose-result">
            {result.success ? <CheckCircle size={16} weight="fill" /> : <XCircle size={16} weight="fill" />}
            {result.message}
          </div>
        )}

        <div className="config-form-actions">
          <button className="config-btn config-btn-save" onClick={handleSend}
            disabled={sending || !form.account_id || !form.to_email || !form.subject || (selectedAcc && !selectedAcc.can_send)}
            data-testid="compose-send-btn">
            {sending ? <><CircleNotch size={14} className="spinning" /> Invio in corso...</> : <><PaperPlaneIcon size={14} /> Invia Email</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- IMPORT PANEL ----
function ImportPanel({ onImportDone }) {
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState("");
  const [jobPhase, setJobPhase] = useState(""); // upload | processing
  const [resumable, setResumable] = useState(null); // { uploadId, filename, size, totalChunks, received }
  const cancelRef = useRef(false);
  const lastFileRef = useRef(null);

  const CHUNK_SIZE = 8 * 1024 * 1024; // 8 MB per chunk — 4x fewer requests vs 2MB

  // On mount: check if there is an interrupted upload we can resume
  useEffect(() => {
    const raw = localStorage.getItem("pst_pending_upload");
    if (raw) {
      try {
        const p = JSON.parse(raw);
        setResumable(p);
      } catch {}
    }
  }, []);

  const pollJob = async (jobId) => {
    while (true) {
      try {
        const { data } = await ax.get(`/import/job/${jobId}`);
        const status = data.status;
        const msg = data.progress_msg || status;
        setProgress(`Elaborazione: ${msg}`);
        if (status === "done") {
          return {
            success: true,
            message: `Importazione completata: ${data.imported} nuove email (${data.total_parsed} totali). ${data.accounts_found} account, ${(data.folders_found || []).length} cartelle.`,
            imported: data.imported,
            total_parsed: data.total_parsed,
            accounts_found: data.accounts_found,
            folders_found: data.folders_found || [],
            structure: data.structure || {},
          };
        }
        if (status === "error") {
          return { success: false, message: data.error || "Errore durante l'elaborazione" };
        }
      } catch (e) {
        // ignore transient poll errors
      }
      await new Promise(r => setTimeout(r, 3000));
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    lastFileRef.current = file;
    const ext = file.name.toLowerCase().split(".").pop();
    if (!["pst", "eml", "mbox"].includes(ext)) {
      setResult({ success: false, message: "Formato non supportato. Usa file .pst, .eml o .mbox" });
      return;
    }
    setImporting(true); setResult(null); setProgress(""); cancelRef.current = false;
    try {
      if (file.size > 5 * 1024 * 1024) {
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        const fileSizeMB = (file.size / (1024*1024)).toFixed(0);
        setJobPhase("upload");
        setProgress(`Preparazione upload (${fileSizeMB} MB, ${totalChunks} parti da 8MB)...`);
        const { data: initData } = await ax.post("/import/init-upload", { filename: file.name, total_size: file.size, total_chunks: totalChunks });
        const uploadId = initData.upload_id;
        const alreadyReceived = new Set(initData.received_chunks || []);
        // Save pending upload info for resume
        localStorage.setItem("pst_pending_upload", JSON.stringify({
          uploadId, filename: file.name, size: file.size, totalChunks, received: alreadyReceived.size,
        }));
        setResumable(null);
        if (initData.resumed && alreadyReceived.size > 0) {
          setProgress(`Upload ripreso: ${alreadyReceived.size}/${totalChunks} parti gia' caricate. Continuo...`);
          await new Promise(r => setTimeout(r, 500));
        }
        let uploadedCount = alreadyReceived.size;
        for (let i = 0; i < totalChunks; i++) {
          if (cancelRef.current) throw new Error("Upload annullato dall'utente");
          if (alreadyReceived.has(i)) continue;
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);
          const chunkForm = new FormData();
          chunkForm.append("file", chunk, `chunk_${i}`);
          uploadedCount += 1;
          const pct = Math.round((uploadedCount / totalChunks) * 100);
          const uploadedMB = Math.round((uploadedCount * (CHUNK_SIZE / (1024*1024))));
          setProgress(`Caricamento: ${Math.min(uploadedMB, parseInt(fileSizeMB))} MB / ${fileSizeMB} MB (${pct}%) - parte ${i + 1} di ${totalChunks}`);
          let ok = false;
          for (let t = 0; t < 10 && !ok; t++) {
            try {
              await ax.post(`/import/upload-chunk/${uploadId}/${i}`, chunkForm, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
              ok = true;
            } catch (ce) {
              if (t < 9) {
                const waitSec = Math.min(60, Math.pow(2, t)); // 1,2,4,8,16,32,60,60,60
                setProgress(`Rete instabile: parte ${i + 1} - riprovo tra ${waitSec}s (tentativo ${t + 2}/10)...`);
                await new Promise(r => setTimeout(r, waitSec * 1000));
              } else {
                // Update localStorage so user can resume later
                localStorage.setItem("pst_pending_upload", JSON.stringify({
                  uploadId, filename: file.name, size: file.size, totalChunks, received: uploadedCount - 1,
                }));
                throw new Error(`Errore caricamento parte ${i + 1} di ${totalChunks} dopo 10 tentativi. Clicca "Riprendi Upload" o riseleziona lo stesso file per continuare da dove si e' fermato.`);
              }
            }
          }
          // Persist progress every 20 chunks in localStorage
          if (i % 20 === 0) {
            localStorage.setItem("pst_pending_upload", JSON.stringify({
              uploadId, filename: file.name, size: file.size, totalChunks, received: uploadedCount,
            }));
          }
          // Tiny delay every 50 chunks to allow browser GC
          if (i % 50 === 49) await new Promise(r => setTimeout(r, 30));
        }
        setJobPhase("processing");
        setProgress("Upload completato! Avvio elaborazione in background...");
        localStorage.removeItem("pst_pending_upload");
        await ax.post(`/import/finalize/${uploadId}`, {}, { timeout: 30000 });
        const jobResult = await pollJob(uploadId);
        setResult(jobResult);
        if (jobResult.success) onImportDone();
      } else {
        setProgress("Caricamento...");
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await ax.post("/import/upload", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
        setResult(data);
        if (data.success) onImportDone();
      }
    } catch (e) {
      setResult({ success: false, message: e.response?.data?.detail || e.message || "Errore durante l'importazione." });
      // Keep resume info available
      const raw = localStorage.getItem("pst_pending_upload");
      if (raw) {
        try { setResumable(JSON.parse(raw)); } catch {}
      }
    } finally { setImporting(false); setProgress(""); setJobPhase(""); }
  };

  const handleResumeClick = () => {
    // Prompt user to reselect the same file - browser security requires user-initiated file selection
    document.getElementById("import-file-input").click();
  };

  const handleDiscardResume = () => {
    localStorage.removeItem("pst_pending_upload");
    setResumable(null);
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]); };
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  return (
    <div className="detail-pane animate-in" data-testid="import-panel" style={{ overflow: "auto" }}>
      <div style={{ padding: "24px 24px 0" }}>
        <h2 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "1.5rem", fontWeight: 900, margin: "0 0 4px", display: "flex", alignItems: "center", gap: 10 }}>
          <FileArrowUp size={24} weight="duotone" />
          Importa Email da Outlook
        </h2>
        <p style={{ color: "var(--text-tertiary)", fontSize: "0.8125rem", margin: "0 0 20px" }}>
          Importa il tuo archivio email da file Outlook PST, EML o MBOX.
        </p>
      </div>

      <div style={{ padding: "0 24px" }}>
        {resumable && !importing && (
          <div className="config-help" style={{ marginBottom: 12, background: "#FEF3C7", border: "1px solid #FCD34D" }} data-testid="import-resume-banner">
            <h4 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "0.8125rem", fontWeight: 700, margin: "0 0 8px", color: "#92400E" }}>
              Upload interrotto: <code>{resumable.filename}</code>
            </h4>
            <div style={{ fontSize: "0.75rem", color: "#78350F", lineHeight: 1.6, marginBottom: 10 }}>
              {resumable.received} / {resumable.totalChunks} parti gia' caricate ({Math.round((resumable.received / resumable.totalChunks) * 100)}%).
              Riseleziona lo stesso file (<code>{resumable.filename}</code>) per riprendere da dove si e' fermato.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="config-btn" onClick={handleResumeClick} data-testid="import-resume-btn">
                <FileArrowUp size={14} /> Riseleziona file per riprendere
              </button>
              <button className="config-btn" onClick={handleDiscardResume} style={{ background: "transparent", color: "#92400E" }} data-testid="import-discard-resume-btn">
                Scarta
              </button>
            </div>
          </div>
        )}

        <div className={`import-dropzone ${dragOver ? "dragover" : ""} ${importing ? "importing" : ""}`}
          onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
          data-testid="import-dropzone">
          {importing ? (
            <>
              <CircleNotch size={40} className="spinning" style={{ color: "var(--accent-pec)" }} />
              <p className="import-dropzone-title">Importazione in corso...</p>
              <p className="import-dropzone-sub">{progress || "Questo potrebbe richiedere alcuni minuti per file grandi."}</p>
            </>
          ) : (
            <>
              <Upload size={40} style={{ color: "var(--text-tertiary)", opacity: 0.5 }} />
              <p className="import-dropzone-title">Trascina qui il file oppure clicca per selezionare</p>
              <p className="import-dropzone-sub">Formati supportati: .pst (Outlook), .eml, .mbox</p>
              <input type="file" accept=".pst,.eml,.mbox" style={{ display: "none" }} id="import-file-input"
                onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); }} data-testid="import-file-input" />
              <button className="config-btn" style={{ marginTop: 12 }} onClick={() => document.getElementById("import-file-input").click()} data-testid="import-select-btn">
                <FileArrowUp size={14} /> Seleziona File
              </button>
            </>
          )}
        </div>

        {result && (
          <div className={`config-result ${result.success ? "config-result-ok" : "config-result-error"}`} style={{ marginTop: 16 }} data-testid="import-result">
            {result.success ? <CheckCircle size={16} weight="fill" /> : <XCircle size={16} weight="fill" />}
            {result.message}
          </div>
        )}

        {result && result.success && result.folders_found && result.folders_found.length > 0 && (
          <div className="config-help" style={{ marginTop: 12, background: "#F0FDF4" }} data-testid="import-structure">
            <h4 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "0.8125rem", fontWeight: 700, margin: "0 0 8px" }}>
              <FolderOpen size={14} style={{ marginRight: 4 }} /> Struttura importata
            </h4>
            <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", lineHeight: 1.8 }}>
              <p style={{ margin: "0 0 4px" }}><strong>{result.accounts_found}</strong> account trovati, <strong>{result.folders_found.length}</strong> cartelle:</p>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {result.folders_found.slice(0, 20).map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
                {result.folders_found.length > 20 && <li>... e altre {result.folders_found.length - 20} cartelle</li>}
              </ul>
            </div>
          </div>
        )}

        <div className="config-help" style={{ marginTop: 20 }}>
          <h4 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "0.8125rem", fontWeight: 700, margin: "0 0 8px" }}>
            <Warning size={14} style={{ marginRight: 4 }} /> Come esportare da Outlook
          </h4>
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: "0.75rem", color: "var(--text-secondary)", lineHeight: 1.8 }}>
            <li><strong>File PST (consigliato):</strong> In Outlook vai su File {'>'} Apri ed esporta {'>'} Importa/Esporta {'>'} Esporta in un file {'>'} File di dati di Outlook (.pst)</li>
            <li><strong>File EML:</strong> Trascina le email da Outlook in una cartella del PC — vengono salvate come file .eml</li>
            <li><strong>File MBOX:</strong> Usato da Thunderbird e altri client — esporta la casella in formato MBOX</li>
            <li>I file PST grandi (oltre 1GB) potrebbero richiedere diversi minuti per l'importazione</li>
            <li>Le email duplicate non vengono importate due volte (deduplicazione automatica)</li>
            <li><strong>Struttura preservata:</strong> account, cartelle e sottocartelle di Outlook vengono riconosciuti e mantenuti nella piattaforma</li>
            <li><strong>Riprendi upload:</strong> se l'upload viene interrotto (rete, browser chiuso), riseleziona lo stesso file — l'upload riprende automaticamente da dove si era fermato</li>
          </ul>
        </div>

        <div className="config-help" style={{ marginTop: 12, background: "#EFF6FF", border: "1px solid #BFDBFE" }} data-testid="import-cli-hint">
          <h4 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "0.8125rem", fontWeight: 700, margin: "0 0 8px", color: "#1E40AF" }}>
            File molto grandi (oltre 5 GB)? Import diretto dal server
          </h4>
          <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", lineHeight: 1.7 }}>
            <p style={{ margin: "0 0 6px" }}>Per PST di grandi dimensioni (10GB, 31GB, 100GB, ecc.) il metodo piu' rapido e affidabile e' importare direttamente dal server, saltando il browser:</p>
            <ol style={{ margin: 0, paddingLeft: 18 }}>
              <li>Trasferisci il file PST sul server (via SCP, volume Docker, o pannello hosting) in <code>/tmp/archivio.pst</code></li>
              <li>Esegui questo comando sul server:<br/>
                <code style={{ display: "block", marginTop: 4, padding: "6px 8px", background: "#1F2937", color: "#D1FAE5", borderRadius: 4, fontSize: "0.7rem" }}>python /app/backend/import_pst.py --file /tmp/archivio.pst</code>
              </li>
              <li>Lo script stampa il progresso in console e importa tutto nel database</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- BACKUP PANEL ----
function BackupPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await ax.get("/admin/backups");
      setData(data);
    } catch (e) {
      setMessage({ success: false, text: e.response?.data?.detail || "Errore caricamento backup" });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    setCreating(true); setMessage(null);
    try {
      const { data: r } = await ax.post("/admin/backup/create");
      const sizeStr = r.size < 1024*1024
        ? `${(r.size/1024).toFixed(1)} KB`
        : `${(r.size/(1024*1024)).toFixed(2)} MB`;
      setMessage({ success: true, text: `Backup creato: ${r.filename} (${sizeStr})` });
      await load();
    } catch (e) {
      setMessage({ success: false, text: e.response?.data?.detail || "Errore creazione backup" });
    } finally { setCreating(false); }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Eliminare definitivamente il backup ${filename}?`)) return;
    try {
      await ax.delete(`/admin/backup/${encodeURIComponent(filename)}`);
      setMessage({ success: true, text: `Backup ${filename} eliminato` });
      await load();
    } catch (e) {
      setMessage({ success: false, text: e.response?.data?.detail || "Errore eliminazione" });
    }
  };

  const handleDownload = async (filename) => {
    try {
      const response = await ax.get(`/admin/backup/download/${encodeURIComponent(filename)}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setMessage({ success: false, text: "Errore download backup" });
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "medium" }); }
    catch { return iso; }
  };

  const humanSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024*1024) return `${(bytes/1024).toFixed(1)} KB`;
    if (bytes < 1024*1024*1024) return `${(bytes/(1024*1024)).toFixed(1)} MB`;
    return `${(bytes/(1024*1024*1024)).toFixed(2)} GB`;
  };

  return (
    <div className="detail-pane animate-in" data-testid="backup-panel" style={{ overflow: "auto" }}>
      <div style={{ padding: "24px 24px 0" }}>
        <h2 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "1.5rem", fontWeight: 900, margin: "0 0 4px", display: "flex", alignItems: "center", gap: 10 }}>
          <FloppyDisk size={24} weight="duotone" />
          Backup Database
        </h2>
        <p style={{ color: "var(--text-tertiary)", fontSize: "0.8125rem", margin: "0 0 20px" }}>
          Backup automatico giornaliero dell'intero database (email, account, tag, config, firme, utenti). Conservazione ultimi {data?.retention || 14} backup.
        </p>
      </div>

      <div style={{ padding: "0 24px 24px" }}>
        {/* Info scheduler + azione manuale */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 16 }}>
          <div className="config-help" style={{ margin: 0 }}>
            <div style={{ fontSize: "0.7rem", color: "var(--text-tertiary)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Ultimo backup</div>
            <div style={{ fontSize: "0.875rem", fontWeight: 600, marginTop: 4 }}>
              <Clock size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
              {formatDate(data?.status?.last_success)}
            </div>
          </div>
          <div className="config-help" style={{ margin: 0 }}>
            <div style={{ fontSize: "0.7rem", color: "var(--text-tertiary)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Backup totali</div>
            <div style={{ fontSize: "0.875rem", fontWeight: 600, marginTop: 4 }}>
              <Archive size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
              {data?.count || 0} file · {humanSize(data?.total_size_bytes || 0)}
            </div>
          </div>
          <div className="config-help" style={{ margin: 0 }}>
            <div style={{ fontSize: "0.7rem", color: "var(--text-tertiary)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Spazio libero</div>
            <div style={{ fontSize: "0.875rem", fontWeight: 600, marginTop: 4 }}>
              <FloppyDisk size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
              {humanSize(data?.disk_free_bytes || 0)}
            </div>
          </div>
        </div>

        <button className="config-btn" onClick={handleCreate} disabled={creating} data-testid="backup-create-btn"
          style={{ marginBottom: 16 }}>
          {creating ? <><CircleNotch size={14} className="spinning" /> Creazione backup...</> : <><CloudArrowDown size={14} /> Crea Backup Adesso</>}
        </button>

        {message && (
          <div className={`config-result ${message.success ? "config-result-ok" : "config-result-error"}`}
               style={{ marginBottom: 16 }} data-testid="backup-message">
            {message.success ? <CheckCircle size={16} weight="fill" /> : <XCircle size={16} weight="fill" />}
            {message.text}
          </div>
        )}

        {data?.status?.last_error && (
          <div className="config-result config-result-error" style={{ marginBottom: 16 }}>
            <Warning size={16} weight="fill" />
            Ultimo errore: {data.status.last_error}
          </div>
        )}

        {/* Lista backup */}
        <h3 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "1rem", fontWeight: 700, margin: "12px 0" }}>
          Backup disponibili
        </h3>

        {loading ? (
          <div style={{ padding: 32, textAlign: "center", color: "var(--text-tertiary)" }}>
            <CircleNotch size={24} className="spinning" /> Caricamento...
          </div>
        ) : !data?.backups?.length ? (
          <div className="config-help" style={{ textAlign: "center", color: "var(--text-tertiary)" }}>
            Nessun backup disponibile. Il primo backup automatico verra' eseguito entro 24h dall'avvio del server, oppure puoi crearne uno manualmente.
          </div>
        ) : (
          <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }} data-testid="backup-list">
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8125rem" }}>
              <thead style={{ background: "var(--bg-tertiary)" }}>
                <tr>
                  <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 700 }}>Data</th>
                  <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 700 }}>Nome file</th>
                  <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700 }}>Dimensione</th>
                  <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, width: 180 }}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {data.backups.map((b, i) => (
                  <tr key={b.filename} style={{ borderTop: i > 0 ? "1px solid var(--border)" : "none" }} data-testid={`backup-row-${i}`}>
                    <td style={{ padding: "10px 12px" }}>{formatDate(b.created_at)}</td>
                    <td style={{ padding: "10px 12px", fontFamily: "monospace", fontSize: "0.75rem" }}>{b.filename}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right" }}>{humanSize(b.size_bytes)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right" }}>
                      <button className="config-btn-secondary" onClick={() => handleDownload(b.filename)}
                        style={{ marginRight: 6, padding: "4px 10px", fontSize: "0.7rem" }}
                        data-testid={`backup-download-${i}`}>
                        <DownloadSimple size={12} /> Scarica
                      </button>
                      <button className="config-btn-secondary" onClick={() => handleDelete(b.filename)}
                        style={{ padding: "4px 10px", fontSize: "0.7rem", color: "#DC2626" }}
                        data-testid={`backup-delete-${i}`}>
                        <Trash size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="config-help" style={{ marginTop: 20, background: "#EFF6FF", border: "1px solid #BFDBFE" }}>
          <h4 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "0.8125rem", fontWeight: 700, margin: "0 0 8px", color: "#1E40AF" }}>
            Come ripristinare un backup
          </h4>
          <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", lineHeight: 1.7 }}>
            <p style={{ margin: "0 0 6px" }}>Scarica il file <code>.archive.gz</code> e, in caso di disaster recovery, esegui sul server:</p>
            <code style={{ display: "block", padding: "8px 10px", background: "#1F2937", color: "#D1FAE5", borderRadius: 4, fontSize: "0.7rem" }}>
              mongorestore --uri="$MONGO_URL" --archive=backup_YYYYMMDD_HHMMSS.archive.gz --gzip --drop
            </code>
            <p style={{ margin: "6px 0 0" }}><strong>Consiglio:</strong> scarica periodicamente il backup piu' recente su un supporto esterno (NAS, cloud personale) per doppia sicurezza.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- AUDIT LOG PANEL ----
const AUDIT_ACTION_LABELS = {
  login_success: "Login riuscito",
  login_failed: "Login FALLITO",
  logout: "Logout",
  user_register: "Registrazione utente",
  user_create: "Creazione utente",
  user_delete: "Eliminazione utente",
  email_delete: "Eliminazione email",
  email_delete_bulk: "Eliminazione email (blocco)",
  account_create: "Creazione account email",
  account_delete: "Eliminazione account email",
  backup_create: "Backup creato",
  backup_download: "Backup scaricato",
  backup_delete: "Backup eliminato",
  audit_export: "Export audit log",
};

function AuditLogPanel() {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: "", user_email: "", target_type: "", date_from: "", date_to: "" });
  const [page, setPage] = useState(0);
  const LIMIT = 50;

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset: page * LIMIT });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const [{ data: d }, { data: s }] = await Promise.all([
        ax.get(`/admin/audit-logs?${params}`),
        ax.get("/admin/audit-logs/stats"),
      ]);
      setData(d);
      setStats(s);
    } catch (e) {
      console.error("Errore audit log", e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  const applyFilters = () => { setPage(0); load(); };
  const clearFilters = () => { setFilters({ action: "", user_email: "", target_type: "", date_from: "", date_to: "" }); setPage(0); setTimeout(load, 100); };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const response = await ax.get(`/admin/audit-logs/export.csv?${params}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      link.download = `audit_log_${ts}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      // Reload to include the export event itself
      setTimeout(load, 500);
    } catch (e) {
      console.error("Errore export CSV", e);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "medium" }); }
    catch { return iso; }
  };

  const total = data?.total || 0;
  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="detail-pane animate-in" data-testid="audit-panel" style={{ overflow: "auto" }}>
      <div style={{ padding: "24px 24px 0" }}>
        <h2 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "1.5rem", fontWeight: 900, margin: "0 0 4px", display: "flex", alignItems: "center", gap: 10 }}>
          <ListBullets size={24} weight="duotone" />
          Audit Log
        </h2>
        <p style={{ color: "var(--text-tertiary)", fontSize: "0.8125rem", margin: "0 0 20px" }}>
          Tracciamento eventi per compliance e segreto professionale. Retention: {stats?.retention_days || 365} giorni. Solo amministratori possono consultarlo.
        </p>
      </div>

      <div style={{ padding: "0 24px 24px" }}>
        {/* Stats cards */}
        {stats && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 16 }}>
            <div className="config-help" style={{ margin: 0 }}>
              <div style={{ fontSize: "0.7rem", color: "var(--text-tertiary)", fontWeight: 700, textTransform: "uppercase" }}>Eventi totali</div>
              <div style={{ fontSize: "1.5rem", fontWeight: 900, marginTop: 4 }}>{stats.total_events}</div>
            </div>
            <div className="config-help" style={{ margin: 0 }}>
              <div style={{ fontSize: "0.7rem", color: "var(--text-tertiary)", fontWeight: 700, textTransform: "uppercase" }}>Ultimi 30 giorni</div>
              <div style={{ fontSize: "1.5rem", fontWeight: 900, marginTop: 4 }}>{stats.last_30_days}</div>
            </div>
            <div className="config-help" style={{ margin: 0, background: stats.failed_logins_30d > 5 ? "#FEE2E2" : undefined }}>
              <div style={{ fontSize: "0.7rem", color: "var(--text-tertiary)", fontWeight: 700, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 4 }}>
                {stats.failed_logins_30d > 5 && <ShieldWarning size={12} weight="fill" style={{ color: "#DC2626" }} />}
                Login falliti (30gg)
              </div>
              <div style={{ fontSize: "1.5rem", fontWeight: 900, marginTop: 4, color: stats.failed_logins_30d > 5 ? "#DC2626" : undefined }}>{stats.failed_logins_30d}</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="config-help" style={{ marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, alignItems: "end" }}>
            <div>
              <label style={{ fontSize: "0.7rem", fontWeight: 700, color: "var(--text-tertiary)", textTransform: "uppercase" }}>Azione</label>
              <select className="config-input" value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value }))} data-testid="audit-filter-action">
                <option value="">Tutte</option>
                {(data?.filter_options?.actions || []).map(a => <option key={a} value={a}>{AUDIT_ACTION_LABELS[a] || a}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: "0.7rem", fontWeight: 700, color: "var(--text-tertiary)", textTransform: "uppercase" }}>Utente</label>
              <select className="config-input" value={filters.user_email} onChange={e => setFilters(f => ({ ...f, user_email: e.target.value }))} data-testid="audit-filter-user">
                <option value="">Tutti</option>
                {(data?.filter_options?.users || []).map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: "0.7rem", fontWeight: 700, color: "var(--text-tertiary)", textTransform: "uppercase" }}>Tipo target</label>
              <select className="config-input" value={filters.target_type} onChange={e => setFilters(f => ({ ...f, target_type: e.target.value }))} data-testid="audit-filter-target">
                <option value="">Tutti</option>
                {(data?.filter_options?.target_types || []).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: "0.7rem", fontWeight: 700, color: "var(--text-tertiary)", textTransform: "uppercase" }}>Da</label>
              <input type="date" className="config-input" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} data-testid="audit-filter-from" />
            </div>
            <div>
              <label style={{ fontSize: "0.7rem", fontWeight: 700, color: "var(--text-tertiary)", textTransform: "uppercase" }}>A</label>
              <input type="date" className="config-input" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} data-testid="audit-filter-to" />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button className="config-btn" onClick={applyFilters} data-testid="audit-apply-filters"><Funnel size={14} /> Applica</button>
            <button className="config-btn-secondary" onClick={clearFilters} data-testid="audit-clear-filters"><X size={14} /> Reset</button>
            <button className="config-btn-secondary" onClick={handleExportCSV} style={{ marginLeft: "auto" }} data-testid="audit-export-csv">
              <FileCsv size={14} /> Esporta CSV
            </button>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ padding: 32, textAlign: "center", color: "var(--text-tertiary)" }}>
            <CircleNotch size={24} className="spinning" /> Caricamento...
          </div>
        ) : !data?.logs?.length ? (
          <div className="config-help" style={{ textAlign: "center", color: "var(--text-tertiary)" }}>
            Nessun evento con i filtri selezionati.
          </div>
        ) : (
          <>
            <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "auto" }} data-testid="audit-list">
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8125rem" }}>
                <thead style={{ background: "var(--bg-tertiary)" }}>
                  <tr>
                    <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 700, whiteSpace: "nowrap" }}>Data / Ora</th>
                    <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 700 }}>Utente</th>
                    <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 700 }}>Azione</th>
                    <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 700 }}>Descrizione</th>
                    <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 700 }}>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {data.logs.map((l, i) => (
                    <tr key={l.id} style={{ borderTop: i > 0 ? "1px solid var(--border)" : "none", background: !l.success ? "#FEF2F2" : undefined }} data-testid={`audit-row-${i}`}>
                      <td style={{ padding: "8px 12px", whiteSpace: "nowrap", fontFamily: "monospace", fontSize: "0.75rem" }}>{formatDate(l.timestamp)}</td>
                      <td style={{ padding: "8px 12px" }}>{l.user_email || <em style={{ color: "var(--text-tertiary)" }}>anonimo</em>}</td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{
                          display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: "0.7rem", fontWeight: 700,
                          background: l.success ? "#DBEAFE" : "#FEE2E2", color: l.success ? "#1E40AF" : "#991B1B",
                        }}>
                          {AUDIT_ACTION_LABELS[l.action] || l.action}
                        </span>
                      </td>
                      <td style={{ padding: "8px 12px", fontSize: "0.8125rem", color: "var(--text-secondary)" }}>{l.description || "-"}</td>
                      <td style={{ padding: "8px 12px", fontFamily: "monospace", fontSize: "0.7rem", color: "var(--text-tertiary)" }}>{l.ip || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, fontSize: "0.8125rem", color: "var(--text-tertiary)" }}>
              <span data-testid="audit-total">{total} eventi totali · Pagina {page + 1} di {totalPages || 1}</span>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="config-btn-secondary" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} data-testid="audit-prev-page">← Precedente</button>
                <button className="config-btn-secondary" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="audit-next-page">Successiva →</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---- SECURITY (2FA) PANEL ----
function SecurityPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("idle"); // idle | setup | verify | backup | disable
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [disablePwd, setDisablePwd] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await ax.get("/auth/2fa/status");
      setStatus(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setMsg(null);
    try {
      const { data } = await ax.post("/auth/2fa/setup");
      setSetupData(data); setStep("setup"); setVerifyCode("");
    } catch (e) {
      setMsg({ success: false, text: e.response?.data?.detail || "Errore setup" });
    }
  };

  const verifyAndEnable = async (e) => {
    e.preventDefault();
    setMsg(null);
    try {
      const { data } = await ax.post("/auth/2fa/enable", { totp_code: verifyCode.trim() });
      setBackupCodes(data.backup_codes);
      setStep("backup");
      await load();
    } catch (e) {
      setMsg({ success: false, text: e.response?.data?.detail || "Codice non valido" });
    }
  };

  const doDisable = async (e) => {
    e.preventDefault();
    setMsg(null);
    try {
      await ax.post("/auth/2fa/disable", { password: disablePwd, totp_code: disableCode.trim() });
      setMsg({ success: true, text: "2FA disattivata correttamente" });
      setStep("idle"); setDisablePwd(""); setDisableCode("");
      await load();
    } catch (e) {
      setMsg({ success: false, text: e.response?.data?.detail || "Errore disabilitazione" });
    }
  };

  const downloadBackupCodes = () => {
    if (!backupCodes) return;
    const content = `# Codici di backup 2FA - Archivio Email Studio Legale Zilioli\n# Data: ${new Date().toISOString()}\n# CONSERVA IN UN POSTO SICURO. Ogni codice puo' essere usato una sola volta.\n\n${backupCodes.join("\n")}\n`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "backup-codes-2fa.txt";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="detail-pane animate-in" data-testid="security-panel" style={{ overflow: "auto" }}>
      <div style={{ padding: "24px 24px 0" }}>
        <h2 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "1.5rem", fontWeight: 900, margin: "0 0 4px", display: "flex", alignItems: "center", gap: 10 }}>
          <ShieldCheck size={24} weight="duotone" />
          Sicurezza — Autenticazione 2FA
        </h2>
        <p style={{ color: "var(--text-tertiary)", fontSize: "0.8125rem", margin: "0 0 20px" }}>
          Proteggi il tuo account con un secondo fattore (Google Authenticator, Authy, Microsoft Authenticator).
        </p>
      </div>

      <div style={{ padding: "0 24px 24px", maxWidth: 640 }}>
        {msg && (
          <div className={`config-result ${msg.success ? "config-result-ok" : "config-result-error"}`} style={{ marginBottom: 16 }} data-testid="security-msg">
            {msg.success ? <CheckCircle size={16} weight="fill" /> : <XCircle size={16} weight="fill" />}
            {msg.text}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 32, textAlign: "center" }}><CircleNotch size={24} className="spinning" /></div>
        ) : status?.enabled && step === "idle" ? (
          <div className="config-help" style={{ background: "#F0FDF4", border: "1px solid #86EFAC" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <CheckCircle size={28} weight="fill" style={{ color: "#16A34A" }} />
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>2FA attiva</div>
                <div style={{ fontSize: "0.8rem", color: "var(--text-tertiary)" }}>Il tuo account e' protetto da autenticazione a due fattori.</div>
              </div>
            </div>
            <p style={{ fontSize: "0.8125rem", margin: "8px 0" }}>Codici di backup rimanenti: <strong>{status.remaining_backup_codes}</strong> / 10</p>
            <button className="config-btn config-btn-delete" onClick={() => setStep("disable")} data-testid="security-disable-btn">
              <XCircle size={14} /> Disattiva 2FA
            </button>
          </div>
        ) : status?.enabled && step === "disable" ? (
          <div className="config-help">
            <h4 style={{ margin: "0 0 12px", fontWeight: 700 }}>Conferma disattivazione 2FA</h4>
            <p style={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}>Per motivi di sicurezza inserisci la tua password e un codice OTP corrente.</p>
            <form onSubmit={doDisable}>
              <div className="form-group">
                <label>Password</label>
                <input className="config-input" type="password" value={disablePwd} onChange={e => setDisablePwd(e.target.value)} required data-testid="security-disable-password" />
              </div>
              <div className="form-group">
                <label>Codice OTP corrente</label>
                <input className="config-input" type="text" value={disableCode} onChange={e => setDisableCode(e.target.value)} maxLength={6} placeholder="000000" required data-testid="security-disable-otp" style={{ fontFamily: "monospace", letterSpacing: "0.2em" }} />
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button type="submit" className="config-btn config-btn-delete" data-testid="security-confirm-disable"><XCircle size={14} /> Disattiva definitivamente</button>
                <button type="button" className="config-btn-secondary" onClick={() => { setStep("idle"); setDisablePwd(""); setDisableCode(""); }}>Annulla</button>
              </div>
            </form>
          </div>
        ) : !status?.enabled && step === "idle" ? (
          <div className="config-help" style={{ background: "#FEF3C7", border: "1px solid #FDE68A" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <ShieldWarning size={28} weight="fill" style={{ color: "#D97706" }} />
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>2FA non attiva</div>
                <div style={{ fontSize: "0.8rem", color: "var(--text-tertiary)" }}>Il tuo account e' protetto solo dalla password. Consigliamo di attivare la 2FA.</div>
              </div>
            </div>
            <button className="config-btn" onClick={startSetup} data-testid="security-enable-btn">
              <ShieldCheck size={14} /> Attiva 2FA
            </button>
          </div>
        ) : step === "setup" && setupData ? (
          <div className="config-help">
            <h4 style={{ margin: "0 0 8px", fontWeight: 700 }}>Passo 1 — Scansiona il QR code</h4>
            <p style={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}>{setupData.instructions}</p>
            <div style={{ textAlign: "center", padding: "12px 0" }}>
              <img src={`data:image/png;base64,${setupData.qr_code_base64}`} alt="QR code 2FA" style={{ maxWidth: 220, border: "1px solid var(--border)", borderRadius: 8 }} data-testid="security-qr-image" />
            </div>
            <details style={{ marginBottom: 12, fontSize: "0.8125rem" }}>
              <summary style={{ cursor: "pointer", fontWeight: 600 }}>Non puoi scansionare il QR? Inserisci manualmente</summary>
              <div style={{ padding: "8px 0", fontFamily: "monospace", fontSize: "0.85rem" }}>
                <div>Account: <strong>{setupData.account}</strong></div>
                <div>Emittente: <strong>{setupData.issuer}</strong></div>
                <div style={{ marginTop: 6 }}>Chiave: <code style={{ background: "#F3F4F6", padding: "2px 6px" }} data-testid="security-manual-secret">{setupData.secret}</code></div>
              </div>
            </details>
            <h4 style={{ margin: "16px 0 8px", fontWeight: 700 }}>Passo 2 — Conferma con un codice</h4>
            <form onSubmit={verifyAndEnable}>
              <div className="form-group">
                <label>Inserisci il codice a 6 cifre generato dall'app</label>
                <input className="config-input" type="text" value={verifyCode} onChange={e => setVerifyCode(e.target.value)} maxLength={6} placeholder="000000" required autoFocus data-testid="security-verify-code" style={{ fontFamily: "monospace", letterSpacing: "0.3em", textAlign: "center", fontSize: "1.4rem" }} />
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button type="submit" className="config-btn" data-testid="security-verify-btn"><CheckCircle size={14} /> Conferma e Attiva</button>
                <button type="button" className="config-btn-secondary" onClick={() => { setStep("idle"); setSetupData(null); }}>Annulla</button>
              </div>
            </form>
          </div>
        ) : step === "backup" && backupCodes ? (
          <div className="config-help" style={{ background: "#F0FDF4", border: "1px solid #86EFAC" }}>
            <h4 style={{ margin: "0 0 8px", fontWeight: 700, color: "#166534" }}>
              <CheckCircle size={18} weight="fill" style={{ verticalAlign: "middle", marginRight: 6 }} />
              2FA attivata! Salva i tuoi codici di backup
            </h4>
            <p style={{ fontSize: "0.8125rem", color: "#166534" }}>
              <strong>Questi codici NON verranno mostrati di nuovo.</strong> Salvali in un posto sicuro (password manager, cassaforte, chiavetta USB).
              Ogni codice puo' essere usato UNA VOLTA sola se perdi accesso all'app authenticator.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, padding: 12, background: "white", borderRadius: 8, fontFamily: "monospace", fontSize: "1rem", margin: "12px 0" }} data-testid="security-backup-codes">
              {backupCodes.map((c, i) => (
                <div key={i} style={{ padding: "6px 10px", background: "#F9FAFB", borderRadius: 4, textAlign: "center", fontWeight: 600 }}>{c}</div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="config-btn" onClick={downloadBackupCodes} data-testid="security-download-backup"><DownloadSimple size={14} /> Scarica come TXT</button>
              <button className="config-btn-secondary" onClick={() => { setStep("idle"); setSetupData(null); setBackupCodes(null); }} data-testid="security-done-backup">Ho salvato i codici</button>
            </div>
          </div>
        ) : null}

        <div className="config-help" style={{ marginTop: 20, background: "#EFF6FF", border: "1px solid #BFDBFE" }}>
          <h4 style={{ margin: "0 0 8px", fontWeight: 700, color: "#1E40AF" }}>App consigliate</h4>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: "0.8125rem", color: "var(--text-secondary)", lineHeight: 1.8 }}>
            <li><strong>Google Authenticator</strong> (Android / iOS) — semplice e gratuita</li>
            <li><strong>Authy</strong> (Android / iOS / Desktop) — sincronizza tra dispositivi</li>
            <li><strong>Microsoft Authenticator</strong> (Android / iOS) — utile se usi anche M365</li>
            <li><strong>1Password / Bitwarden</strong> — se usi un password manager con TOTP integrato</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ---- TAGS PANEL ----
function TagsPanel({ onTagsChanged }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [form, setForm] = useState({ name: "", color: "#2563EB" });
  const [error, setError] = useState("");

  const PRESET_COLORS = ["#2563EB", "#DC2626", "#D97706", "#059669", "#7C3AED", "#DB2777", "#0891B2", "#4F46E5", "#0D9488", "#9333EA", "#EA580C", "#65A30D"];

  const loadTags = useCallback(async () => {
    try {
      const { data } = await ax.get("/tags/stats");
      setTags(data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTags(); }, [loadTags]);

  const resetForm = () => { setForm({ name: "", color: "#2563EB" }); setError(""); };

  const handleCreate = async () => {
    setError("");
    if (!form.name.trim()) { setError("Inserisci un nome per la pratica"); return; }
    try {
      await ax.post("/tags", form);
      setShowForm(false); resetForm(); loadTags(); onTagsChanged();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
  };

  const handleUpdate = async () => {
    setError("");
    if (!form.name.trim()) { setError("Inserisci un nome per la pratica"); return; }
    try {
      await ax.put(`/tags/${editingTag.id}`, form);
      setEditingTag(null); resetForm(); loadTags(); onTagsChanged();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
  };

  const handleDelete = async (id, name, count) => {
    const msg = count > 0
      ? `Eliminare la pratica "${name}"? Verra' rimossa da ${count} email associate.`
      : `Eliminare la pratica "${name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await ax.delete(`/tags/${id}`);
      loadTags(); onTagsChanged();
    } catch (e) { alert(formatApiError(e.response?.data?.detail)); }
  };

  const startEdit = (t) => {
    setEditingTag(t);
    setForm({ name: t.name, color: t.color });
    setShowForm(false); setError("");
  };

  return (
    <div className="detail-pane animate-in" data-testid="tags-panel" style={{ overflow: "auto" }}>
      <div style={{ padding: "24px 24px 0" }}>
        <h2 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "1.5rem", fontWeight: 900, margin: "0 0 4px", display: "flex", alignItems: "center", gap: 10 }}>
          <TagSimple size={24} weight="duotone" />
          Gestione Pratiche / Etichette
        </h2>
        <p style={{ color: "var(--text-tertiary)", fontSize: "0.8125rem", margin: "0 0 20px" }}>
          Crea e organizza le pratiche per catalogare le email per cliente o per causa. ({tags.length} pratiche)
        </p>
      </div>

      <div style={{ padding: "0 24px" }}>
        {loading && <p style={{ color: "var(--text-tertiary)", fontSize: "0.8125rem" }}>Caricamento...</p>}

        <div className="tags-manage-grid" data-testid="tags-list">
          {tags.map(t => (
            <div key={t.id} className="tag-manage-card" data-testid={`tag-card-${t.id}`}>
              <div className="tag-manage-color" style={{ background: t.color }} />
              <div className="tag-manage-info">
                <div className="tag-manage-name">{t.name}</div>
                <div className="tag-manage-count">{t.email_count} {t.email_count === 1 ? "email" : "email"}</div>
              </div>
              <div className="user-card-actions">
                <button className="config-btn" onClick={() => startEdit(t)} data-testid={`edit-tag-${t.id}`}>
                  <PencilSimple size={14} /> Modifica
                </button>
                <button className="config-btn config-btn-delete" onClick={() => handleDelete(t.id, t.name, t.email_count)} data-testid={`delete-tag-${t.id}`}>
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {tags.length === 0 && !loading && !showForm && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "var(--text-tertiary)" }}>
            <TagSimple size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
            <p style={{ fontSize: "0.875rem", margin: "0 0 4px" }}>Nessuna pratica creata</p>
            <p style={{ fontSize: "0.75rem" }}>Crea la prima pratica per iniziare a catalogare le email.</p>
          </div>
        )}
      </div>

      {/* Add button */}
      {!showForm && !editingTag && (
        <div style={{ padding: "16px 24px" }}>
          <button className="config-add-btn" onClick={() => { setShowForm(true); resetForm(); setEditingTag(null); }} data-testid="add-tag-btn">
            <Plus size={16} /> Nuova Pratica / Etichetta
          </button>
        </div>
      )}

      {/* Create / Edit form */}
      {(showForm || editingTag) && (
        <div className="config-form" style={{ margin: "0 24px" }} data-testid="tag-form">
          <h3 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "1rem", fontWeight: 700, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}>
            {editingTag ? <><PencilSimple size={18} /> Modifica: {editingTag.name}</> : <><Plus size={18} /> Nuova Pratica</>}
          </h3>

          {error && <div className="config-result config-result-error" style={{ marginBottom: 12 }}><XCircle size={16} weight="fill" /> {error}</div>}

          <div className="config-form-row">
            <div className="config-form-field">
              <label className="config-label">Nome Pratica / Etichetta</label>
              <input className="config-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="es. Pratica Rossi c. Bianchi, Diritto del Lavoro..." data-testid="tag-name-input" />
            </div>
          </div>

          <div className="config-form-section">
            <label className="config-label">Colore</label>
            <div className="color-presets">
              {PRESET_COLORS.map(c => (
                <button key={c} className={`color-preset-btn ${form.color === c ? "active" : ""}`}
                  style={{ background: c }} onClick={() => setForm(p => ({ ...p, color: c }))}
                  data-testid={`color-${c.replace("#", "")}`} />
              ))}
              <input type="color" value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))}
                style={{ width: 28, height: 28, border: "1px solid var(--border-default)", borderRadius: 2, cursor: "pointer", padding: 0 }}
                data-testid="tag-color-custom" title="Colore personalizzato" />
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <span className="config-label" style={{ margin: 0 }}>Anteprima:</span>
            <span style={{ background: form.color, color: "#fff", padding: "3px 10px", borderRadius: 2, fontSize: "0.75rem", fontWeight: 600 }}>
              {form.name || "Nome pratica"}
            </span>
          </div>

          <div className="config-form-actions">
            <button className="config-btn config-btn-save" onClick={editingTag ? handleUpdate : handleCreate} disabled={!form.name.trim()} data-testid="save-tag-btn">
              <CheckCircle size={14} /> {editingTag ? "Salva Modifiche" : "Crea Pratica"}
            </button>
            <button className="config-btn config-btn-cancel" onClick={() => { setShowForm(false); setEditingTag(null); resetForm(); }} data-testid="cancel-tag-btn">
              <X size={14} /> Annulla
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- USERS PANEL ----
function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [error, setError] = useState("");

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await ax.get("/admin/users");
      setUsers(data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const resetForm = () => { setForm({ name: "", email: "", password: "", role: "user" }); setError(""); setShowPwd(false); };

  const handleCreate = async () => {
    setError("");
    try {
      await ax.post("/admin/users", form);
      setShowForm(false); resetForm(); loadUsers();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
  };

  const handleUpdate = async () => {
    setError("");
    try {
      const payload = {};
      if (form.name) payload.name = form.name;
      if (form.email) payload.email = form.email;
      if (form.password) payload.password = form.password;
      if (form.role) payload.role = form.role;
      await ax.put(`/admin/users/${editingUser.id}`, payload);
      setEditingUser(null); resetForm(); loadUsers();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Eliminare l'utente "${name}"? Questa azione e' irreversibile.`)) return;
    try {
      await ax.delete(`/admin/users/${id}`);
      loadUsers();
    } catch (e) { alert(formatApiError(e.response?.data?.detail)); }
  };

  const startEdit = (u) => {
    setEditingUser(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role });
    setShowForm(false); setError("");
  };

  return (
    <div className="detail-pane animate-in" data-testid="users-panel" style={{ overflow: "auto" }}>
      <div style={{ padding: "24px 24px 0" }}>
        <h2 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "1.5rem", fontWeight: 900, margin: "0 0 4px", display: "flex", alignItems: "center", gap: 10 }}>
          <Users size={24} weight="duotone" />
          Gestione Utenti
        </h2>
        <p style={{ color: "var(--text-tertiary)", fontSize: "0.8125rem", margin: "0 0 20px" }}>
          Crea, modifica ed elimina gli operatori dello studio. ({users.length} utenti)
        </p>
      </div>

      <div style={{ padding: "0 24px" }}>
        {loading && <p style={{ color: "var(--text-tertiary)", fontSize: "0.8125rem" }}>Caricamento...</p>}

        {/* Users list */}
        <div className="users-grid" data-testid="users-list">
          {users.map(u => (
            <div key={u.id} className="user-card" data-testid={`user-card-${u.id}`}>
              <div className="user-card-avatar" style={{ background: u.role === "admin" ? "var(--accent-warning)" : "var(--brand-primary)" }}>
                {getInitials(u.name)}
              </div>
              <div className="user-card-info">
                <div className="user-card-name">
                  {u.name}
                  {u.role === "admin" && <Crown size={14} weight="fill" style={{ color: "var(--accent-warning)", marginLeft: 6 }} />}
                </div>
                <div className="user-card-email">{u.email}</div>
                <div className="user-card-meta">
                  <span className={`user-role-badge ${u.role === "admin" ? "role-admin" : "role-user"}`}>
                    {u.role === "admin" ? "Amministratore" : "Operatore"}
                  </span>
                  {u.created_at && <span className="user-card-date">Creato: {formatDate(u.created_at)}</span>}
                </div>
              </div>
              <div className="user-card-actions">
                <button className="config-btn" onClick={() => startEdit(u)} data-testid={`edit-user-${u.id}`}>
                  <PencilSimple size={14} /> Modifica
                </button>
                <button className="config-btn config-btn-delete" onClick={() => handleDelete(u.id, u.name)} data-testid={`delete-user-${u.id}`}>
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add user button */}
      {!showForm && !editingUser && (
        <div style={{ padding: "16px 24px" }}>
          <button className="config-add-btn" onClick={() => { setShowForm(true); resetForm(); setEditingUser(null); }} data-testid="add-user-btn">
            <UserPlus size={16} /> Aggiungi Nuovo Utente
          </button>
        </div>
      )}

      {/* Create / Edit form */}
      {(showForm || editingUser) && (
        <div className="config-form" style={{ margin: "0 24px" }} data-testid="user-form">
          <h3 style={{ fontFamily: "'Chivo', sans-serif", fontSize: "1rem", fontWeight: 700, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}>
            {editingUser ? <><PencilSimple size={18} /> Modifica Utente: {editingUser.name}</> : <><UserPlus size={18} /> Nuovo Utente</>}
          </h3>

          {error && <div className="config-result config-result-error" style={{ marginBottom: 12 }}><XCircle size={16} weight="fill" /> {error}</div>}

          <div className="config-form-row">
            <div className="config-form-field">
              <label className="config-label">Nome Completo</label>
              <input className="config-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="es. Avv. Mario Rossi" data-testid="user-name-input" />
            </div>
            <div className="config-form-field">
              <label className="config-label">Email</label>
              <input className="config-input" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="es. m.rossi@zilioli.it" data-testid="user-email-input" />
            </div>
          </div>

          <div className="config-form-row">
            <div className="config-form-field">
              <label className="config-label">{editingUser ? "Nuova Password (lascia vuoto per non cambiare)" : "Password"}</label>
              <div className="password-input-wrapper">
                <input className="config-input" type={showPwd ? "text" : "password"} value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder={editingUser ? "Lascia vuoto per non cambiare" : "Inserisci password"} data-testid="user-password-input" />
                <button type="button" className="password-toggle" onClick={() => setShowPwd(!showPwd)} data-testid="user-pwd-toggle">
                  {showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="config-form-field" style={{ maxWidth: 220 }}>
              <label className="config-label">Ruolo</label>
              <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                <button className={`config-provider-btn ${form.role === "user" ? "active" : ""}`} onClick={() => setForm(p => ({ ...p, role: "user" }))} data-testid="role-user-btn">
                  <Users size={14} /> Operatore
                </button>
                <button className={`config-provider-btn ${form.role === "admin" ? "active" : ""}`} onClick={() => setForm(p => ({ ...p, role: "admin" }))} data-testid="role-admin-btn">
                  <Crown size={14} /> Admin
                </button>
              </div>
            </div>
          </div>

          <div className="config-form-actions">
            <button className="config-btn config-btn-save" onClick={editingUser ? handleUpdate : handleCreate}
              disabled={!form.name || !form.email || (!editingUser && !form.password)}
              data-testid="save-user-btn">
              <CheckCircle size={14} /> {editingUser ? "Salva Modifiche" : "Crea Utente"}
            </button>
            <button className="config-btn config-btn-cancel" onClick={() => { setShowForm(false); setEditingUser(null); resetForm(); }} data-testid="cancel-user-btn">
              <X size={14} /> Annulla
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- MAIN APP ----
function Dashboard() {
  const { user, logout } = useContext(AuthContext);
  const [accounts, setAccounts] = useState([]);
  const [tags, setTags] = useState([]);
  const [emails, setEmails] = useState([]);
  const [totalEmails, setTotalEmails] = useState(0);
  const [stats, setStats] = useState(null);
  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [activeAccount, setActiveAccount] = useState(null);
  const [activeTag, setActiveTag] = useState(null);
  const [activeView, setActiveView] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [folder, setFolder] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedEmailIds, setSelectedEmailIds] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [accRes, tagRes, statsRes] = await Promise.all([
        ax.get("/accounts/stats"),
        ax.get("/tags"),
        ax.get("/stats"),
      ]);
      setAccounts(accRes.data);
      setTags(tagRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error("Errore caricamento dati:", err);
    }
  }, []);

  const loadEmails = useCallback(async () => {
    setLoading(true);
    try {
      const body = { page: 1, per_page: 200 };
      if (searchQuery) body.query = searchQuery;
      if (activeAccount) body.account_id = activeAccount;
      if (activeTag) body.tag_id = activeTag;
      if (folder) body.folder = folder;
      if (dateFrom) body.date_from = dateFrom + "T00:00:00Z";
      if (dateTo) body.date_to = dateTo + "T23:59:59Z";
      if (activeView === "attachments") body.has_attachments = true;
      const { data } = await ax.post("/emails/search", body);
      let filtered = data.emails;
      if (activeView === "unread") {
        filtered = filtered.filter(e => !e.read);
      }
      setEmails(filtered);
      setTotalEmails(activeView === "unread" ? filtered.length : data.total);
    } catch (err) {
      console.error("Errore caricamento email:", err);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, activeAccount, activeTag, folder, activeView, dateFrom, dateTo]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadEmails(); }, [loadEmails]);

  const handleTagAdd = async (emailId, tagId) => {
    await ax.post(`/emails/${emailId}/tags`, { tag_id: tagId });
    loadEmails();
    loadData();
  };

  const handleTagRemove = async (emailId, tagId) => {
    await ax.delete(`/emails/${emailId}/tags/${tagId}`);
    loadEmails();
    loadData();
  };

  const handleSelectEmail = (id) => {
    setSelectedEmailId(id);
    if (activeView === "stats") setActiveView("all");
    setEmails(prev => prev.map(e => e.id === id ? { ...e, read: true } : e));
  };

  const toggleSelectEmail = (id) => {
    setSelectedEmailIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAllEmails = () => {
    if (selectedEmailIds.length === emails.length) {
      setSelectedEmailIds([]);
    } else {
      setSelectedEmailIds(emails.map(e => e.id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedEmailIds.length === 0) return;
    if (!window.confirm(`Eliminare ${selectedEmailIds.length} email selezionate? Questa azione e' irreversibile.`)) return;
    try {
      await ax.post("/emails/delete-bulk", { email_ids: selectedEmailIds });
      setSelectedEmailIds([]);
      setSelectedEmailId(null);
      loadEmails();
      loadData();
    } catch (e) {
      alert("Errore durante l'eliminazione");
    }
  };

  const [syncingAccountId, setSyncingAccountId] = useState(null);

  const handleSyncAccount = async (accountId) => {
    setSyncingAccountId(accountId);
    try {
      const { data } = await ax.post(`/config/accounts/${accountId}/sync`);
      if (data.success) {
        loadEmails();
        loadData();
      }
    } catch (e) {
      // Account might not be configured for sync (demo accounts)
    } finally {
      setSyncingAccountId(null);
    }
  };

  return (
    <div className="app-layout" data-testid="app-layout">
      <Sidebar
        accounts={accounts} tags={tags}
        activeAccount={activeAccount} setActiveAccount={setActiveAccount}
        activeTag={activeTag} setActiveTag={setActiveTag}
        activeView={activeView} setActiveView={setActiveView}
        user={user} onLogout={logout} stats={stats}
        onSyncAccount={handleSyncAccount} syncingAccountId={syncingAccountId}
      />
      <EmailList
        emails={emails} total={totalEmails}
        selectedId={selectedEmailId} onSelect={handleSelectEmail}
        searchQuery={searchQuery} setSearchQuery={setSearchQuery}
        folder={folder} setFolder={setFolder}
        dateFrom={dateFrom} setDateFrom={setDateFrom}
        dateTo={dateTo} setDateTo={setDateTo}
        loading={loading} tags={tags}
        activeAccount={activeAccount} accounts={accounts}
        selectedEmailIds={selectedEmailIds} toggleSelectEmail={toggleSelectEmail}
        selectAllEmails={selectAllEmails} handleDeleteSelected={handleDeleteSelected}
      />
      {activeView === "stats" ? (
        <StatsView stats={stats} accounts={accounts} />
      ) : activeView === "config" ? (
        <ConfigPanel onAccountsChanged={loadData} />
      ) : activeView === "users" ? (
        <UsersPanel />
      ) : activeView === "tags-manage" ? (
        <TagsPanel onTagsChanged={loadData} />
      ) : activeView === "compose" ? (
        <ComposePanel onEmailSent={() => { loadData(); loadEmails(); }} />
      ) : activeView === "signatures" ? (
        <SignaturesPanel />
      ) : activeView === "import" ? (
        <ImportPanel onImportDone={() => { loadData(); loadEmails(); }} />
      ) : activeView === "backup" ? (
        <BackupPanel />
      ) : activeView === "audit" ? (
        <AuditLogPanel />
      ) : activeView === "security" ? (
        <SecurityPanel />
      ) : (
        <EmailDetail
          emailId={selectedEmailId} tags={tags} allTags={tags}
          onTagAdd={handleTagAdd} onTagRemove={handleTagRemove}
          onEmailSent={() => { loadData(); loadEmails(); }}
          onTagCreated={loadData}
        />
      )}
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    ax.get("/auth/me").then(r => setUser(r.data)).catch(() => setUser(false)).finally(() => setChecking(false));
  }, []);

  const login = (data) => {
    if (data.token) {
      ax.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
      localStorage.setItem("auth_token", data.token);
    }
    setUser(data);
  };

  const logout = async () => {
    try { await ax.post("/auth/logout"); } catch {}
    delete ax.defaults.headers.common["Authorization"];
    localStorage.removeItem("auth_token");
    setUser(false);
  };

  if (checking) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "'IBM Plex Sans', sans-serif", color: "var(--text-tertiary)" }}>
        Caricamento...
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {user ? <Dashboard /> : <LoginPage />}
    </AuthContext.Provider>
  );
}

export default App;
